// 管理画面の言語切り替え（日本語・中文・English）  2026-10-28
//   - 選んだ言語は端末に保存（localStorage 'admin_lang'）。次回もその言語で開く
//   - 既存の t('admin.xxx')（react-i18next）も同じ言語に合わせる
//   - 画面ごとの文言はこのファイルの辞書に追加していく（未登録のキーは日本語で表示）
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import i18n from '../i18n';

export type AdminLang = 'ja' | 'zh' | 'en';
export const ADMIN_LANGS: { code: AdminLang; label: string }[] = [
  { code: 'ja', label: '日本語' },
  { code: 'zh', label: '中文' },
  { code: 'en', label: 'English' },
];
const KEY = 'admin_lang';

function detect(): AdminLang {
  try {
    const s = localStorage.getItem(KEY);
    if (s === 'ja' || s === 'zh' || s === 'en') return s;
  } catch { /* 既定へ */ }
  return 'ja';
}

const ja = {
  // 清掃チェックリスト（2026-10-31）
  'clnCk.result': 'チェックリスト（{n}項目 確認済み）', 'clnCk.noResult': 'チェックリストの記録はありません（チェックリスト導入前の報告、または管理者による手動完了）', 'clnCk.checkedBy': '報告者: {s}　{t}',
  'clnCk.tab': 'チェックリスト', 'clnCk.title': '清掃チェックリスト', 'clnCk.desc': '清掃者は完了報告のとき、ここで登録した項目をすべてチェックしないと報告できません。全物件共通の項目に加えて、物件ごとの項目も追加できます。',
  'clnCk.scope': '対象', 'clnCk.common': '全物件共通', 'clnCk.inherit': 'この物件には、全物件共通の {n} 項目も表示されます。', 'clnCk.empty': '項目がありません',
  'clnCk.inactive': '無効', 'clnCk.save': '保存', 'clnCk.cancel': 'キャンセル', 'clnCk.edit': '編集', 'clnCk.disable': '無効にする', 'clnCk.enable': '有効にする',
  'clnCk.del': '削除', 'clnCk.confirmDel': '「{s}」を削除しますか？（過去の報告の記録は残ります）', 'clnCk.ph': '例: 浴室の排水口の髪の毛を取る', 'clnCk.add': '追加',
  // 共通・レイアウト
  loading: '読み込み中...', menu: 'メニュー', logout: 'ログアウト', language: '言語', legacyUser: '共有パスワード（移行期間）',
  'nav.dashboard': 'ダッシュボード', 'nav.calendar': 'カレンダー', 'nav.cleaning': '清掃管理', 'nav.reservations': '予約管理',
  'nav.messages': 'ゲスト連絡', 'nav.properties': '施設管理', 'nav.passport': 'パスポート管理', 'nav.checkin': 'チェックイン管理',
  'nav.settings': '設定', 'nav.adminUsers': '管理者アカウント', 'nav.otaReview': 'OTA同期の確認', 'rsv.otaIncomplete': '予約情報補完待ち', 'rsv.otaCancelSuspected': 'キャンセル疑い', 'rsv.csConfidence': '確度', 'rsv.csStreak': '連続{p0}回未検出', 'rsv.cs_HIGH': '高', 'rsv.cs_MEDIUM': '中', 'rsv.cs_LOW': '低', 'rsv.cs_UID_MISSING': 'OTAのカレンダーからイベントが消えました', 'rsv.cs_NO_COVERAGE': 'OTAのカレンダーにこの日程のイベントがありません', 'rsv.cs_PARTIAL_COVERAGE': 'OTAの日程と一部しか一致しません', 'rsv.cs_SOURCE_BLOCK_REMOVED': '登録元のAirbnbブロックが消えました', 'rsv.cs_FEED_EMPTIED': 'OTAのカレンダーに今後のイベントがありません', 'rsv.cs_DATE_MISMATCH': 'OTAのカレンダーと日程が違います', 'rsv.otaActiveInFeed': 'OTAでは有効', 'rsv.cs_ACTIVE_IN_FEED': 'キャンセル済みですが、OTAでは有効な予約です',
  'rsv.otaReview': '要確認（OTA）', 'rsv.otaDateChanged': '日程変更あり', 'rsv.otaMissing': 'OTAのカレンダーから消えています', 'rsv.otaOverlap': '他の予約と日付が重なっています', 'rsv.otaUnset': '未入力', 'rsv.otaCode': 'OTA予約番号', 'rsv.otaGoReview': 'OTA同期の確認で対応',
  // ログイン
  'login.title': '管理画面ログイン', 'login.id': 'ログインID', 'login.pw': 'パスワード', 'login.submit': 'ログイン', 'login.checking': '確認中...',
  'login.noServer': 'APIサーバーに接続できません', 'login.failed': 'ログインに失敗しました',
  // ダッシュボード
  'db.attention': '⚠ 要対応の予約が {n} 件あります',
  'db.attentionText': '仮押さえの期限切れ後に決済され、日程を確保できなかった予約です。日程の再確保または返金を行ってください。',
  'db.viewList': '一覧を見る →', 'db.total': '全予約件数', 'db.newWeek': '1週間以内の新規予約', 'db.newWeekSub': '過去7日間に受け付けた予約',
  'db.pending': '承認待ち', 'db.availTitle': '本日 〜 翌月末の空き日数', 'db.availTip': '{name}（{v}/{t}日空き）', 'db.days': '日', 'db.daysUnit': '日',
  'db.noProps': '物件がありません', 'db.checkin': 'チェックイン', 'db.checkout': 'チェックアウト', 'db.staying': '宿泊中', 'db.vacant': '空き',
  'db.cleaning': '清掃', 'db.cleaningErr': '清掃の情報を取得できませんでした', 'db.cleanToday': '本日の清掃件数',
  'db.cleanTodaySub': '完了 {d} ／ 残り {r}', 'db.cleanTomorrow': '明日の清掃件数', 'db.openCleaning': '清掃管理を開く →',
  'db.unassigned': '今週の未割当', 'db.unassignedSub': '今日から7日間・担当者なし',
  'db.upcoming': '1週間以内のチェックイン・チェックアウト', 'db.noUpcoming': '本日から1週間以内の予定はありません',
  'db.colProp': '物件名', 'db.colGuests': '人数', 'db.guestsN': '{n}名', 'db.colPassport': 'パスポート登録', 'db.colCheckinSt': 'チェックイン状態',
  'db.registered': '登録済', 'db.notRegistered': '未登録', 'db.csNone': '未', 'db.csProgress': '手続中', 'db.csDone': '完了',
  "cln.stPending": "🧹 未着手",
  "cln.stProgress": "🔄 作業中",
  "cln.stDone": "✅ 完了",
  "cln.stReview": "⚠️ 要確認",
  "cln.stCancelled": "取消",
  "cln.err": "エラーが発生しました ({s})",
  "clnA.0": "未割当",
  "clnD.0": "読み込み中…",
  "clnD.1": "本日の状況",
  "clnD.2": "↻ 更新",
  "clnD.3": "今日の清掃",
  "clnD.4": "件",
  "clnD.5": "完了",
  "clnD.6": "／ 残り",
  "clnD.7": "期限切れの未完了",
  "clnD.8": "昨日以前で未着手・作業中",
  "clnD.9": "要確認",
  "clnD.10": "管理者の判断が必要",
  "clnD.11": "今週の未割当",
  "clnD.12": "今日から7日間",
  "clnD.13": "明日の清掃",
  "clnD.14": "⚠ 期限切れの未完了",
  "clnD.15": "⚠️ 要確認",
  "clnD.16": "今後1週間の清掃タスク（{p0}〜{p1}）",
  "clnD.17": "今後1週間の清掃予定はありません。",
  "clnD.18": "※ 行をクリックすると、カレンダーでその日の詳細を開きます（状態の変更・担当者の変更はそこで行えます）。",
  "clnD.19": "清掃日",
  "clnD.20": "物件",
  "clnD.21": "担当者",
  "clnD.22": "予定時間",
  "clnD.23": "状態",
  "clnT.0": "今週",
  "clnT.1": "来週",
  "clnT.2": "先週",
  "clnT.3": "今月",
  "clnT.4": "先月",
  "clnT.5": "期間指定",
  "clnT.6": "期間を正しく指定してください。",
  "clnT.7": "期間",
  "clnT.8": "開始日",
  "clnT.10": "終了日",
  "clnT.11": "物件",
  "clnT.12": "すべての物件",
  "clnT.13": "担当者",
  "clnT.14": "すべての担当者",
  "clnT.15": "未割当のみ",
  "clnT.16": "（無効）",
  "clnT.17": "状態",
  "clnT.18": "完了と要確認",
  "clnT.19": "完了のみ",
  "clnT.20": "要確認のみ",
  "clnT.21": "未完了すべて",
  "clnT.22": "未着手",
  "clnT.23": "作業中",
  "clnT.24": "要確認",
  "clnT.25": "：",
  "clnT.26": "件",
  "clnT.27": "／ 備品代 合計",
  "clnT.28": "円",
  "clnT.29": "（500件までを表示しています。期間を絞ってください）",
  "clnT.30": "読み込み中…",
  "clnT.31": "条件に合う清掃予定はありません。",
  "clnT.32": "清掃日",
  "clnT.33": "開始",
  "clnT.34": "終了",
  "clnT.35": "結果",
  "clnT.36": "備品代",
  "clnT.37": "承認",
  "clnT.38": "{p0}円",
  "clnT.39": "確認",
  "clnT.40": "確認する",
  "clnT.41": "詳細",
  "clnT.42": "予定時間",
  "clnT.43": "※ 行をクリックすると、カレンダーでその日の詳細を開きます。",
  "clnT.44": "✔ 承認済",
  "clnT.45": "✖ 差し戻し中",
  "clnT.46": "未承認",
  "clnT.47": "✔ 承認",
  "clnT.48": "✖ 却下（差し戻し）",
  "clnT.49": "💬 コメント",
  "clnT.50": "却下するときはコメント（やり直してほしい内容）を入力してください。",
  "clnT.51": "コメントを入力してください。",
  "clnT.52": "（担当者{p0}人に通知しました）",
  "clnT.53": "（担当者が未割当のため、通知はしていません）",
  "clnT.54": "承認しました{p0}",
  "clnT.55": "却下して差し戻しました{p0}",
  "clnT.56": "コメントを送りました{p0}",
  "clnT.57": "清掃結果の確認",
  "clnT.58": "作業時間",
  "clnT.59": "要確認の理由",
  "clnT.60": "連絡メモ",
  "clnT.61": "備品購入代金",
  "clnT.62": "管理者メモ",
  "clnT.63": "やりとりの履歴",
  "clnT.64": "管理者",
  "clnT.65": "清掃者",
  "clnT.66": "・",
  "clnT.67": "コメント（清掃担当者に通知されます。",
  "clnT.68": "却下のときは必須",
  "clnT.69": "）",
  "clnT.70": "例: 浴室の鏡に水垢が残っていました。拭き直しをお願いします。",
  "clnT.71": "担当者への連絡事項",
  "clnT.72": "※ 承認・却下は「完了」の清掃だけできます。コメントは送れます。",
  "clnT.73": "却下して差し戻しますか？",
  "clnT.74": "状態が「作業中」に戻り、担当者がやり直して再度完了報告します。終了時刻の記録は消えます。",
  "clnT.75": "やめる",
  "clnT.76": "処理中…",
  "clnT.77": "却下して差し戻す",
  "clnT.78": "カレンダーで開く",
  "clnT.79": "💬 コメントだけ送る",
  "clnT.80": "✖ 却下",
  "clnT.81": "✔ 承認済み",
  "clnT.82": "閉じる",
  "clnT.83": "写真（{p0}枚）",
  "clnT.84": "清掃前",
  "clnT.85": "清掃後",
  "clnT.86": "忘れ物",
  "clnT.87": "破損・汚れ",
  "clnT.88": "その他",
  "clnT.89": "写真 {p0}枚",
  "clnM.0": "エラーが発生しました ({p0})",
  "clnM.1": "パスワードは8文字以上にしてください",
  "clnM.2": "パスワードが一致しません",
  "clnM.3": "「{p0}」のログインを設定しました。ログインURL: {p1}/cleaner",
  "clnM.4": "「{p0}」を保存しました",
  "clnM.5": "（担当者がいなかった清掃予定 {p0}件に割り当てました）",
  "clnM.6": "「{p0}」を無効にします。\n今日以降の未着手の清掃予定からは外れます（過去の作業記録は残ります）。よろしいですか？",
  "clnM.7": "「{p0}」を有効にしました",
  "clnM.8": "（{p0}件の清掃予定に割り当てました）",
  "clnM.9": "「{p0}」を無効にしました",
  "clnM.10": "（{p0}件の清掃予定から外しました）",
  "clnM.11": "物件で絞り込み",
  "clnM.12": "すべての物件",
  "clnM.13": "＋ 担当者を登録",
  "clnM.14": "読み込み中…",
  "clnM.15": "担当者はまだ登録されていません。",
  "clnM.16": "この物件の担当者はいません。",
  "clnM.17": "無効",
  "clnM.18": "予定",
  "clnM.19": "件 ／ 完了",
  "clnM.20": "件",
  "clnM.21": "📍 希望エリア:",
  "clnM.22": "🔑 ログインID:",
  "clnM.23": "🔑 ログイン未設定",
  "clnM.24": "担当物件:",
  "clnM.25": "なし",
  "clnM.26": "編集",
  "clnM.27": "ログイン再設定",
  "clnM.28": "ログイン設定",
  "clnM.29": "無効にする",
  "clnM.30": "有効にする",
  "clnM.31": "※ 担当者は削除せず「無効」にします。物件を割り当てると、その物件の担当者がいない今日以降の清掃予定に自動で入ります。",
  "clnM.32": "清掃者ログインの設定（",
  "clnM.33": "）",
  "clnM.34": "ログインID（英数字と . _ @ -、3〜64文字）",
  "clnM.35": "新しいパスワード（8文字以上）",
  "clnM.36": "新しいパスワード（確認）",
  "clnM.37": "※ ログインURLは",
  "clnM.38": "です。IDとパスワードを担当者に伝えてください。再設定すると、その人のログインは一度切れます。",
  "clnM.39": "キャンセル",
  "clnM.40": "保存中…",
  "clnM.41": "保存",
  "clnM.42": "担当者を登録",
  "clnM.43": "担当者を編集",
  "clnM.44": "名前 *",
  "clnM.45": "電話",
  "clnM.46": "メール",
  "clnM.47": "希望担当エリア",
  "clnM.48": "例: 新宿区・中野区",
  "clnM.49": "住所（任意）",
  "clnM.50": "担当物件",
  "clnP.0": "自社",
  "clnP.1": "手動追加",
  "clnP.2": "🧹 未着手",
  "clnP.3": "🔄 作業中",
  "clnP.4": "✅ 完了",
  "clnP.5": "⚠️ 要確認",
  "clnP.6": "取消",
  "clnP.7": "清掃スケジュールの取得に失敗しました。",
  "clnP.8": "担当者の変更に失敗しました。",
  "clnP.9": "操作に失敗しました。",
  "clnP.10": "要確認にする理由を入力してください（例: 作業を途中で中断、備品の破損など）",
  "clnP.11": "理由を入力してください。",
  "clnP.12": "終了時刻は開始時刻より後にしてください",
  "clnP.13": "保存に失敗しました。",
  "clnP.14": "清掃日の追加に失敗しました。",
  "clnP.15": "清掃管理",
  "clnP.16": "＋ 清掃日を追加",
  "clnP.17": "🏠 ダッシュボード",
  "clnP.18": "📅 カレンダー",
  "clnP.19": "📋 業務一覧",
  "clnP.20": "✅ 完了一覧",
  "clnP.21": "👤 担当者",
  "clnP.22": "🛎️ チェックイン",
  "clnP.23": "前月",
  "clnP.24": "‹ 前月",
  "clnP.25": "年",
  "clnP.26": "月",
  "clnP.27": "翌月",
  "clnP.28": "翌月 ›",
  "clnP.29": "読み込み中…",
  "clnP.30": "{p0}（{p1}）\n清掃日(チェックアウト): {p2}",
  "clnP.31": "{p0}（{p1}）\nチェックイン: {p2}",
  "clnP.32": "の清掃予定",
  "clnP.33": "この日の清掃予定はありません。",
  "clnP.34": "（",
  "clnP.35": "）",
  "clnP.36": "清掃日(チェックアウト):",
  "clnP.37": "予定:",
  "clnP.39": "未設定",
  "clnP.40": "／ チェックイン:",
  "clnP.41": "実績:",
  "clnP.42": "⚠️ 要確認の理由:",
  "clnP.43": "メモ:",
  "clnP.44": "担当:",
  "clnP.45": "（無効）",
  "clnP.46": "未割当",
  "clnP.47": "作業開始",
  "clnP.48": "完了",
  "clnP.49": "要確認",
  "clnP.50": "未着手に戻す",
  "clnP.51": "担当者",
  "clnP.52": "予定時刻・メモ",
  "clnP.53": "この清掃予定を取消にしますか？",
  "clnP.54": "予約を見る",
  "clnP.55": "🛎️ この日のチェックイン（",
  "clnP.56": "件）",
  "clnP.57": "チェックアウト（清掃日）:",
  "clnP.58": "担当者の変更",
  "clnP.59": "／",
  "clnP.60": "担当者が登録されていません。「担当者」タブから登録してください。",
  "clnP.61": "この物件の担当",
  "clnP.62": "無効",
  "clnP.63": "※ 複数人を割り当てた場合、誰か1人が完了報告すれば完了になります。",
  "clnP.64": "キャンセル",
  "clnP.65": "保存",
  "clnP.66": "予定開始",
  "clnP.67": "予定終了",
  "clnP.68": "物件の時刻を入れる（",
  "clnP.69": "時刻を消す",
  "clnP.70": "メモ（清掃担当者にも表示されます）",
  "clnP.71": "保存中…",
  "clnP.72": "清掃日を追加",
  "clnP.73": "予約とは別に、手動で清掃予定を1件追加します（同じ日に既に予定がある場合は追加できません）。",
  "clnP.74": "物件",
  "clnP.75": "選択してください",
  "clnP.76": "清掃日",
  "clnP.77": "予定開始（任意）",
  "clnP.78": "予定終了（任意）",
  "clnP.79": "メモ（任意）",
  "clnP.80": "追加",
  "rsv.0": "予約承認",
  "rsv.1": "予約確定",
  "rsv.2": "キャンセル申請",
  "rsv.3": "キャンセル承認",
  "rsv.4": "パスポート受領済",
  "rsv.5": "本日チェックイン",
  "rsv.6": "宿泊中",
  "rsv.7": "本日チェックアウト",
  "rsv.8": "終了",
  "rsv.9": "OTA予約の登録に失敗しました。",
  "rsv.10": "OTA予約を登録しました。下の「固定URL・予約番号・チェックイン日」をOTAのメッセージ機能でゲストに共有してください。",
  "rsv.11": "管理者認証に失敗しました（401）。ADMIN_PASSWORD_HASH の設定を確認してください。",
  "rsv.12": "予約一覧の取得に失敗しました（{p0}）{p1}",
  "rsv.13": "APIサーバーに接続できません。APIが起動しているか確認してください。",
  "rsv.14": "予約一覧の取得に失敗しました",
  "rsv.15": "操作に失敗しました ({p0})",
  "rsv.16": "キャンセルし、Stripeで全額返金しました。",
  "rsv.17": "キャンセルしました。{p0}",
  "rsv.18": "\nゲストにキャンセル完了メールを送信しました。",
  "rsv.19": "\n※ゲストへのメール送信に失敗しました。ゲスト連絡画面から連絡してください。",
  "rsv.20": "更新に失敗しました ({p0})",
  "rsv.21": "決済待ち",
  "rsv.22": "振込待ち",
  "rsv.23": "確定",
  "rsv.24": "キャンセル",
  "rsv.25": "期限切れ",
  "rsv.26": "返金済み",
  "rsv.27": "⚠ 要対応（期限切れ後に決済）",
  "rsv.28": "⇩ bookingからimport",
  "rsv.29": "⇩ Airbnbからimport",
  "rsv.30": "閉じる",
  "rsv.31": "＋ OTA予約を登録",
  "rsv.32": "再読み込み",
  "rsv.33": "すべて",
  "rsv.34": "物件で絞り込み",
  "rsv.35": "すべての物件",
  "rsv.36": "並び順",
  "rsv.37": "最新予約順",
  "rsv.38": "チェックイン日（昇順）",
  "rsv.39": "チェックイン日（降順）",
  "rsv.40": "表示絞り込み",
  "rsv.41": "表示絞り込み：",
  "rsv.42": "チェックアウト日が過ぎた予約も表示します",
  "rsv.43": "過去の予約も表示",
  "rsv.44": "キャンセル済み・返金済みの予約も表示します",
  "rsv.45": "キャンセル済みも表示",
  "rsv.46": "ステータスで絞り込み（複数選択可）",
  "rsv.47": "ステータス：",
  "rsv.48": "クリア",
  "rsv.49": "物件名",
  "rsv.50": "クリックで昇順・降順を切り替え",
  "rsv.51": "チェックイン",
  "rsv.52": "チェックアウト",
  "rsv.53": "人数",
  "rsv.54": "無料キャンセル期限",
  "rsv.55": "ステータス",
  "rsv.56": "操作",
  "rsv.57": "{p0}名",
  "rsv.58": "詳細",
  "rsv.59": "予約がありません",
  "rsv.60": "予約詳細",
  "rsv.61": "予約番号",
  "rsv.62": "ゲスト名",
  "rsv.63": "メール",
  "rsv.64": "金額",
  "rsv.65": "決済状態:",
  "rsv.66": "手動設定",
  "rsv.67": "キャンセル申請あり",
  "rsv.68": "申請日時:",
  "rsv.69": "（UTC）",
  "rsv.70": "理由:",
  "rsv.71": "まで無料（チェックイン",
  "rsv.72": "日前まで）",
  "rsv.73": "それ以降は返金不可",
  "rsv.74": "この予約の決済を確認し、確定にしますか？（Stripeで支払い済みか確認されます）",
  "rsv.75": "決済確認して確定",
  "rsv.76": "日程を再確保して予約を確定しますか？（空いていない場合は確定できません）",
  "rsv.77": "日程を再確保して確定",
  "rsv.78": "Stripeで全額返金しますか？（取り消しできません）",
  "rsv.79": "全額返金する",
  "rsv.80": "この予約の入金を確認し、確定にしますか？",
  "rsv.81": "入金確認して確定",
  "rsv.82": "無料キャンセル期限内のため、キャンセルと同時にStripeで全額返金します（取り消しできません）。\nよろしいですか？",
  "rsv.83": "この予約をキャンセルしますか？（在庫は解放されます。返金はありません）",
  "rsv.84": "キャンセルを承認",
  "rsv.85": "予約をキャンセル",
  "rsv.86": "ゲストに連絡",
  "ota.0": "その他OTA",
  "ota.1": "物件",
  "ota.2": "選択してください",
  "ota.3": "チェックイン",
  "ota.4": "チェックアウト",
  "ota.5": "宿泊人数",
  "ota.6": "ゲスト名（任意・OTA表示名）",
  "ota.7": "OTA予約番号（任意）",
  "ota.8": "登録してURLを発行",
  "iss.0": "固定URL",
  "iss.1": "アップロードURL",
  "iss.2": "コピーしました",
  "iss.3": "コピー",
  "iss.4": "予約番号入りURL",
  "iss.5": "予約番号",
  "iss.6": "チェックイン日:",
  "iss.7": "※ このURLをゲストに直接お渡しください（メールやOTAメッセージなど）。古いURLは無効化され、これが唯一の有効なURLです。",
  "iss.8": "閉じる",
  "iss.9": "送信先:",
  "iss.10": "件名",
  "iss.11": "本文（テンプレート・編集可）",
  "iss.12": "内容を確認のうえ送信してください",
  "iss.13": "送信中…",
  "iss.14": "メール送信",
  "iss.15": "※ OTAの場合はメール送信は不要です。上記をコピーしてOTAサイトのメッセージでゲストに連絡してください。",
  "abi.0": "確認コード",
  "abi.8": "データ行が見つかりませんでした。",
  "abi.9": "取り込める予約行がありませんでした。",
  "abi.10": "{p0} 件の予約行を読み込みました（物件コード: {p1}）。内容を確認して「取り込む」を押してください。",
  "abi.11": "ファイルの読み込みに失敗しました: {p0}",
  "abi.12": "ファイルを選択してください。",
  "abi.13": "取り込みに失敗しました。",
  "abi.14": "Airbnb から予約を取り込む",
  "abi.15": "Airbnb の予約CSVを選択してください。ファイルはブラウザ内で解析されます。",
  "abi.16": "物件は CSV の「リスティング」列（SD / FT / GW などの物件コード）で自動判別するため、物件の選択は不要です。",
  "abi.17": "金額は取り込みません（予約データのみ）。すでに登録済みの確認コードは重複登録されません。",
  "abi.18": "ファイル（Airbnb 予約CSV）",
  "abi.19": "登録",
  "abi.20": "スキップ",
  "abi.21": "エラー",
  "abi.22": "合計",
  "abi.23": "行",
  "abi.24": "物件",
  "abi.25": "結果",
  "abi.26": "理由",
  "abi.27": "閉じる",
  "abi.28": "キャンセル",
  "abi.29": "取り込み中…",
  "abi.30": "取り込む{p0}",
  "bki.0": "予約番号",
  "bki.9": "データ行が見つかりませんでした。",
  "bki.10": "必要な列が見つかりません: {p0}。Booking.comの予約一覧ファイルか確認してください。",
  "bki.11": "取り込める予約行がありませんでした。",
  "bki.12": "{p0} 件の予約行を読み込みました。内容を確認して「取り込む」を押してください。",
  "bki.13": "ファイルの読み込みに失敗しました: {p0}",
  "bki.14": "物件を選択してください。",
  "bki.15": "ファイルを選択してください。",
  "bki.16": "取り込みに失敗しました。",
  "bki.17": "Booking.com から予約を取り込む",
  "bki.18": "物件を選び、Booking.com の予約一覧ファイル（.xls / .xlsx / .csv）を選択してください。",
  "bki.19": "ファイルはブラウザ内で解析され、各予約がこの物件の予約として登録されます（確定扱い・予約番号を自動発番）。",
  "bki.20": "すでに登録済みの予約番号は重複登録されません。",
  "bki.21": "物件",
  "bki.22": "選択してください",
  "bki.23": "ファイル",
  "bki.24": "登録",
  "bki.25": "スキップ",
  "bki.26": "エラー",
  "bki.27": "合計",
  "bki.28": "行",
  "bki.29": "結果",
  "bki.30": "理由",
  "bki.31": "閉じる",
  "bki.32": "キャンセル",
  "bki.33": "取り込み中…",
  "bki.34": "取り込む{p0}",
  "abi.count": "（{n}件）",
  "bki.count": "（{n}件）",
  "cal.0": "手動ブロック",
  "cal.1": "{p0}年 {p1}月",
  "cal.2": "同期に失敗しました",
  "cal.3": "同期完了: {p0}泊分を取込みました",
  "cal.4": "エラー: {p0}",
  "cal.5": "前の月",
  "cal.6": "次の月",
  "cal.7": "今月",
  "cal.8": "同期する物件をタブで選択してください",
  "cal.9": "この物件のOTAカレンダーを今すぐ同期",
  "cal.10": "同期中…",
  "cal.11": "この物件を今すぐ同期",
  "cal.12": "すべての物件",
  "cal.13": "確定",
  "cal.14": "振込待ち",
  "cal.15": "Stop（Airbnb/OTA取込）",
  "cal.16": "｜ OTA Stop:",
  "cal.18": "件",
  "cal.19": "{p0} / {p1} によりStop（予約不可）",
  "cal.20": "■ Stop（",
  "cal.21": "）",
  "pp.0": "男性",
  "pp.1": "女性",
  "pp.2": "その他",
  "pp.3": "自社",
  "pp.4": "その他OTA",
  "pp.5": "ファイルを取得できませんでした。",
  "pp.6": "名簿CSVの取得に失敗しました。",
  "pp.7": "このファイルを削除します。元に戻せません。よろしいですか？",
  "pp.8": "このゲストの登録情報と提出画像をすべて削除し、未登録の状態に戻します。\n元に戻せません。よろしいですか？",
  "pp.9": "ゲストの登録情報を削除しました。",
  "pp.10": "ゲスト情報の削除に失敗しました。",
  "pp.11": "URLの発行に失敗しました。",
  "pp.12": "OTA予約です。下の「固定URL・予約番号・チェックイン日」をコピーして、OTAサイトのメッセージでゲストに連絡してください。",
  "pp.13": "URLを発行しました。メール文面を確認し、問題なければ「メール送信」を押してください。",
  "pp.14": "即時URLを発行すると、この予約の既存URLはただちに無効化され、\n",
  "pp.15": "新しく発行するURLだけが有効になります。\n",
  "pp.16": "すでにゲストへ古いURLを伝えている場合、そのURLは使えなくなります。\n\n",
  "pp.17": "続行しますか？",
  "pp.18": "救済URLの発行に失敗しました。",
  "pp.19": "有効な固定URL・予約番号を表示しました。OTAサイトのメッセージでゲストにお渡しください。",
  "pp.20": "有効なアップロードURLを1本発行しました。このURLをゲストに直接お渡しください（古いURLは無効化されました）。",
  "pp.21": "メール送信に失敗しました。",
  "pp.22": "（メール送信はスキップされました：送信設定が未構成）",
  "pp.23": "アップロードURLをメールで送信しました。",
  "pp.24": "OTA予約の登録に失敗しました。",
  "pp.25": "OTA予約を登録しました。下の「固定URL・予約番号・チェックイン日」をOTAのメッセージ機能でゲストに共有してください。",
  "pp.26": "パスポート管理",
  "pp.27": "閉じる",
  "pp.28": "＋ OTA予約を登録",
  "pp.29": "URL発行結果",
  "pp.30": "物件名",
  "pp.31": "すべての物件",
  "pp.32": "過去のチェックイン日も表示する",
  "pp.33": "件",
  "pp.34": "読み込み中…",
  "pp.35": "物件名・予約番号",
  "pp.36": "区分",
  "pp.37": "クリックで昇順/降順を切り替え",
  "pp.38": "チェックイン",
  "pp.39": "登録状況",
  "pp.40": "期限",
  "pp.41": "該当する予約がありません",
  "pp.42": "完了",
  "pp.43": "未完了",
  "pp.44": "キャンセル",
  "pp.45": "期限切れ",
  "pp.46": "有効",
  "pp.47": "操作",
  "pp.48": "詳細",
  "pp.49": "URL発行",
  "pp.50": "有効なURLを1本作り直してゲストに直接渡す",
  "pp.51": "即時URL発行",
  "pp.52": "宿泊者名簿をCSVでダウンロード",
  "pp.53": "名簿CSV",
  "pp.54": "（",
  "pp.55": "）",
  "pp.56": "名簿CSVダウンロード",
  "pp.58": "ゲスト",
  "pp.59": "：{p0}",
  "pp.60": "（未登録）",
  "pp.61": "登録済み",
  "pp.62": "このゲストの登録情報と画像を削除して未登録に戻す",
  "pp.63": "登録をリセット",
  "pp.64": "生年月日:",
  "pp.65": "/ 国籍:",
  "pp.66": "住所:",
  "pp.67": "/ 行先地:",
  "pp.68": "ファイルなし",
  "pp.69": "パスポート画像 #{p0}",
  "pp.70": "クリックで拡大",
  "pp.71": "クリックで表示",
  "pp.72": "削除",
  "pp.73": "パスポート画像（拡大）",
  "ci.0": "未提出",
  "ci.1": "提出済み（確認待ち）",
  "ci.2": "確認中",
  "ci.3": "再提出依頼",
  "ci.4": "不一致",
  "ci.5": "確認完了",
  "ci.6": "要対応",
  "ci.7": "未着手",
  "ci.8": "完了",
  "ci.9": "男性",
  "ci.10": "女性",
  "ci.11": "その他",
  "ci.12": "この予約の物件には施設コード（予約番号接頭辞）が未設定です。施設管理で接頭辞（例: SD）を設定してください。",
  "ci.13": "チェックインURLを発行しました。通常は日付入りURLを、OTAの固定URL欄にしか貼れない場合は施設固定URLをゲストにお渡しください。",
  "ci.14": "メール送信に失敗しました。",
  "ci.15": "（メール送信はスキップされました：送信設定が未構成）",
  "ci.16": "アップロードURLをメールで送信しました。",
  "ci.17": "OTA予約の登録に失敗しました。",
  "ci.18": "OTA予約を登録しました。下の「固定URL・予約番号・チェックイン日」をOTAのメッセージ機能でゲストに共有してください。",
  "ci.19": "予約情報を取得できませんでした。",
  "ci.20": "操作に失敗しました: {p0}",
  "ci.21": "再提出を依頼",
  "ci.22": "不一致として記録",
  "ci.23": "要対応として記録",
  "ci.24": "クリックで拡大",
  "ci.25": "読み込み中…",
  "ci.26": "チェックイン管理",
  "ci.27": "閉じる",
  "ci.28": "＋ OTA予約を登録",
  "ci.29": "法令に基づく本人確認です。宿泊者の",
  "ci.30": "顔写真",
  "ci.31": "と",
  "ci.32": "旅券",
  "ci.33": "を照合し、確認結果を記録します。",
  "ci.34": "チェックインURL",
  "ci.35": "チェックインURL（日付入り）",
  "ci.36": "コピーしました",
  "ci.37": "コピー",
  "ci.38": "施設固定URL（日付なし）",
  "ci.39": "施設コード:",
  "ci.40": "／ チェックイン日:",
  "ci.41": "※「日付入り」はゲストが何も入力せず本人確認（顔写真・旅券）へ進めます。",
  "ci.42": "※「施設固定URL」は日付を含まないため、OTAサイトの固定URL欄にそのまま貼れます（ゲストはチェックイン日のみ選択）。",
  "ci.43": "URL発行結果",
  "ci.44": "物件名",
  "ci.45": "すべての物件",
  "ci.46": "過去のチェックイン日も表示する",
  "ci.47": "件",
  "ci.48": "クリックで昇順/降順を切り替え",
  "ci.49": "チェックイン日",
  "ci.50": "施設",
  "ci.51": "本人確認",
  "ci.52": "確認済み",
  "ci.53": "該当する予約がありません",
  "ci.54": "操作",
  "ci.55": "確認",
  "ci.56": "ゲストに渡すチェックインURL（施設コード・日付入り）を発行",
  "ci.57": "URL発行",
  "ci.58": "（",
  "ci.59": "）",
  "ci.61": "本人確認:",
  "ci.62": "宿泊者",
  "ci.63": "：{p0}",
  "ci.64": "（氏名未登録）",
  "ci.65": "提出元:",
  "ci.66": "施設タブレット",
  "ci.67": "ゲスト端末",
  "ci.68": "生年月日:",
  "ci.69": "国籍:",
  "ci.70": "メモ:",
  "ci.71": "本人確認 完了",
  "ci.72": "拡大画像",
  "ci.73": "差し戻し",
  "ci.74": "理由",
  "ci.75": "（任意）",
  "ci.76": "顔と旅券が一致しない 等",
  "ci.77": "本人確認を継続できない理由 等",
  "ci.78": "画像が不鮮明 等",
  "ci.79": "キャンセル",
  "ci.80": "記録する",
  "adm.gender": "/ 性別:",
  "adm.passportNo": "/ 旅券番号:",
  "adm.prevAcc": "/ 前泊地:",
  "msg.0": "決済待ち",
  "msg.1": "振込待ち",
  "msg.2": "確定",
  "msg.3": "キャンセル",
  "msg.4": "期限切れ",
  "msg.5": "返金済み",
  "msg.6": "要対応",
  "msg.7": "送信済み",
  "msg.8": "未送信（メール設定なし）",
  "msg.9": "送信失敗",
  "msg.10": "受信",
  "msg.11": "エラー ({p0})",
  "msg.12": "{p0} とのメッセージ履歴（{p1} 件）を削除しますか？\n※予約データは削除されません。元に戻せません。",
  "msg.13": "削除に失敗しました",
  "msg.14": "APIサーバーに接続できません",
  "msg.15": "取得に失敗しました",
  "msg.16": "ゲスト連絡",
  "msg.17": "再読み込み",
  "msg.18": "名前・メールアドレスで検索",
  "msg.19": "物件で絞り込み",
  "msg.20": "すべての物件",
  "msg.21": "並び順",
  "msg.22": "最新のやりとり順",
  "msg.23": "チェックイン日（昇順）",
  "msg.24": "チェックイン日（降順）",
  "msg.25": "件",
  "msg.26": "読み込み中...",
  "msg.27": "該当するゲストがいません",
  "msg.28": "ゲスト / 最後のメッセージ",
  "msg.29": "チェックイン",
  "msg.30": "状態",
  "msg.31": "最終やりとり",
  "msg.32": "予約なし",
  "msg.33": "(名前なし)",
  "msg.34": "自動",
  "msg.35": "送信",
  "msg.36": "まだやりとりはありません",
  "msg.37": " ・ {p0}",
  "msg.38": " ・ 予約 {p0} 件",
  "msg.40": "パスポート送付済",
  "msg.41": "キャンセル不可",
  "msg.42": "メニュー",
  "msg.43": "削除するメッセージがありません",
  "msg.44": "削除",
  "msg.45": "「{p0}」",
  "msg.46": "このメッセージ",
  "msg.47": "{p0}を削除しますか？\n※元に戻せません。",
  "msg.48": "メッセージを削除しました",
  "msg.49": "件名を入力してください",
  "msg.50": "本文を入力してください",
  "msg.51": "{p0} にメールを送信しますか？",
  "msg.52": "受信メッセージを記録しました",
  "msg.53": "記録しましたが、メール設定（RESEND_API_KEY）がないため実際には送信されていません",
  "msg.54": "送信しました",
  "msg.55": "送信に失敗しました",
  "msg.56": "パスポート状態の更新に失敗しました",
  "msg.57": "← 一覧へ",
  "msg.58": "予約管理で開く",
  "msg.60": "パスポートを受け取ったらチェック",
  "msg.61": "パスポート受領",
  "msg.62": "まだやりとりはありません。下のフォームから最初のメッセージを送信できます。",
  "msg.63": "ゲスト",
  "msg.64": "自動メール",
  "msg.65": "施設",
  "msg.66": "エラー:",
  "msg.67": "⚠ 送信元の認証に失敗しました（なりすましの可能性）。内容をよく確認してください。",
  "msg.68": "差出人:",
  "msg.69": "メールを送信",
  "msg.70": "ゲストからの返信を記録",
  "msg.71": "件名",
  "msg.72": "件名（任意）",
  "msg.73": "関連する予約",
  "msg.74": "メッセージを入力（Ctrl+Enter で送信）",
  "msg.75": "ゲストから届いたメールの本文を貼り付け",
  "msg.76": "処理中...",
  "msg.77": "記録する",
  "prop.0": "有効",
  "prop.1": "無効",
  "prop.2": "最大人数:",
  "prop.3": "名",
  "prop.4": "基本人数:",
  "prop.5": "追加料金:",
  "prop.6": "清掃費:",
  "prop.7": "最低宿泊数:",
  "prop.8": "泊",
  "prop.9": "チェックイン:",
  "prop.10": "チェックアウト:",
  "prop.11": "喫煙:",
  "prop.12": "喫煙可",
  "prop.13": "禁煙",
  "prop.14": "パーティー:",
  "prop.15": "可",
  "prop.16": "不可",
  "prop.17": "無料キャンセル:",
  "prop.18": "チェックイン",
  "prop.19": "日前まで",
  "prop.20": "iCal エクスポートURL",
  "prop.21": "URLをコピー",
  "prop.22": "コピー済み",
  "prop.23": "コピー",
  "prop.24": "未設定 — 「編集」でシークレットを設定すると表示されます",
  "prop.25": "物件名",
  "prop.26": "最大人数",
  "prop.27": "基本人数",
  "prop.28": "追加料金 (円/名)",
  "prop.29": "清掃費 (円)",
  "prop.30": "最低宿泊数 (泊)",
  "prop.31": "チェックアウト",
  "prop.32": "Airbnb / OTA カレンダー取込URL (iCal import)",
  "prop.33": "Airbnb等のカレンダーをエクスポートしたicsのURL。15分ごとに取り込み、在庫をブロックします。",
  "prop.icalBooking": "Booking.com iCal URL",
  "prop.icalBookingHint": "Booking.comのカレンダーをエクスポートしたicsのURL。予約は自動で登録され（ゲスト情報は補完待ち）、Airbnb側の同じ日程のブロックは重複ブロックとして自動で確認済みになります。",
  "prop.34": "iCal エクスポート用シークレット",
  "prop.35": "ランダムな文字列（例: 8f3a1c...）",
  "prop.36": "この物件の書き出しURL（Airbnb側で「他のカレンダーをインポート」に貼付）:",
  "prop.37": "シークレットを設定して保存すると、Airbnbに渡すエクスポートURLが表示されます。",
  "prop.38": "重要ルール・キャンセルポリシー",
  "prop.39": "喫煙",
  "prop.40": "パーティー",
  "prop.41": "無料キャンセル期限（チェックインの何日前まで無料か）",
  "prop.42": "例: 31 の場合、チェックイン日の31日前までは無料キャンセル、それ以降（30日以内）は返金不可。",
  "prop.43": "予約時にゲストへ具体的な期限日（チェックイン日 − この日数）を明示します。",
  "prop.44": "物件詳細",
  "prop.45": "面積（㎡）",
  "prop.46": "ベッドルームの数",
  "prop.47": "リビングルームの数",
  "prop.48": "階数",
  "prop.49": "ダブルベッド数",
  "prop.50": "シングルベッド数",
  "prop.51": "布団",
  "prop.52": "ダブルソファベッド数",
  "prop.53": "シングルソファベッド数",
  "prop.54": "トイレ数",
  "prop.55": "お風呂／シャワー室数",
  "prop.56": "重要ポイント（1行に1つ。ホーム画面のカードに表示）",
  "prop.57": "最大10名\n5ベッドルーム\n屋上バルコニー\n駅から徒歩3分",
  "prop.58": "箇条書きの各項目がカードに ✔ 付きで表示されます。空にすると非表示になります。",
  "prop.59": "「物件を見る」リンク先URL",
  "prop.60": "ホーム画面の物件カードのボタンのリンク先です。空の場合は従来の物件ページ（/property/物件ID）を開きます。",
  "prop.61": "OTA予約番号の接頭辞",
  "prop.62": "例: SD",
  "prop.63": "OTA（Airbnb/Booking.com等）予約の予約番号に使う物件の接頭辞です（例: Stay Dream → SD）。英数字2〜4文字。未設定の物件はOTA予約を登録できません。発行される予約番号は「接頭辞-チェックイン日-ランダム6桁」の形式です（例: SD-20261013-000742）。",
  "prop.64": "カレンダー表示の略語",
  "prop.65": "例: ステイ",
  "prop.66": "/admin/calendar・/admin/cleaning のカレンダー画面で物件を短く表示するための略語です（任意・日本語可）。未設定の場合は物件コード（接頭辞 SD / FT 等）を表示します。",
  "set.0": "基本設定",
  "set.1": "メールテンプレート",
  "set.2": "チェックイン受付端末",
  "set.3": "施設名",
  "set.4": "例: ステイドリームホテル",
  "set.5": "メールの差出人名・本文に使用します。",
  "set.6": "住所",
  "set.7": "例: 東京都渋谷区〇〇1-2-3",
  "set.8": "電話番号",
  "set.9": "例: 03-1234-5678",
  "set.10": "メールアドレス",
  "set.11": "例: info@staydreamhotel.com",
  "set.12": "メールの返信先(Reply-To)・問い合わせ先に使用します。",
  "set.13": "銀行名",
  "set.14": "例: 三菱UFJ銀行",
  "set.15": "支店名",
  "set.16": "例: 渋谷支店",
  "set.17": "口座種別",
  "set.18": "例: 普通",
  "set.19": "口座番号",
  "set.20": "例: 1234567",
  "set.21": "口座名義",
  "set.22": "例: カ）ステイドリーム",
  "set.23": "振込期限（時間）",
  "set.24": "例: 72",
  "set.25": "予約から振込期限までの時間数。振込案内メールに差し込まれます。",
  "set.26": "設定の取得に失敗しました。",
  "set.27": "保存に失敗しました。",
  "set.28": "設定を保存しました。",
  "set.29": "設定",
  "set.30": "施設情報・振込先など、予約確認・振込案内メールに差し込まれる情報を設定します。メールの送信タイミングは「メールテンプレート」タブで設定できます。",
  "set.31": "読み込み中…",
  "set.32": "施設情報",
  "set.33": "振込先情報",
  "set.34": "その他",
  "set.35": "元に戻す",
  "set.36": "保存中…",
  "set.37": "保存",
  "set.38": "管理画面のタイトル",
  "set.39": "例: ステイドリームホテル 管理",
  "set.40": "管理画面の左上に表示する名前です。空欄なら「ステイドリームホテル」を表示します。",
  "set.41": "予約サイトのタイトル",
  "set.42": "例: ステイドリームホテル",
  "set.43": "予約サイト（お客様向け）の左上に表示する名前です。空欄なら「ステイドリームホテル」を表示します。",
  "cp.tab": "リピータークーポン",
  "rsv.coupon": "クーポン",
  "cp.desc": "過去に宿泊したゲストへ案内する、全施設共通のクーポンです。有効なコードは常に1つだけで、新しいコードを保存すると前のコードは直ちに使えなくなります。割引は宿泊料金部分のみ（追加人数料金・清掃料金・消費税は対象外）。",
  "cp.status": "状態",
  "cp.st.NONE": "未登録",
  "cp.st.ACTIVE": "有効",
  "cp.st.PAUSED": "一時停止中",
  "cp.st.NOT_STARTED": "期間前",
  "cp.st.EXPIRED": "期間終了",
  "cp.code": "クーポンコード",
  "cp.codeHint": "英数字とハイフン、3〜30文字。大文字・小文字は区別しません（大文字で保存）。",
  "cp.rate": "割引率（%）",
  "cp.rateHint": "1〜{n}の整数",
  "cp.from": "有効開始日",
  "cp.until": "有効終了日",
  "cp.periodHint": "予約の受付日（日本時間）で判定します。終了日の23:59まで有効。",
  "cp.note": "メモ（社内用）",
  "cp.updated": "最終更新: {t}（{by}）",
  "cp.usage": "このコードでの予約: {n}件",
  "cp.save": "保存",
  "cp.saving": "保存中…",
  "cp.reset": "元に戻す",
  "cp.pause": "一時停止",
  "cp.resume": "再開",
  "cp.confirmReplace": "クーポンコードを変更すると、現在のコード（{s}）は直ちに利用できなくなります。変更しますか？",
  "cp.confirmPause": "クーポン（{s}）を一時停止します。新しい予約では使えなくなります（確定済みの予約は変わりません）。よろしいですか？",
  "cp.saved": "保存しました",
  "cp.loadErr": "読み込みに失敗しました",
  "cp.saveErr": "保存に失敗しました",
  "cp.history": "過去のコード",
  "cp.noHistory": "なし",
  "cp.guestText": "ゲスト向け案内文（コピー用）",
  "cp.copy": "コピー",
  "cp.copied": "コピーしました",
  "dev.0": "チェックイン受付端末（iPad）セットアップ",
  "dev.1": "現場に設置するiPad向けの初期設定です。施設を選んで「このiPadに設定」を押すと、",
  "dev.2": "チェックイン受付用のURLが生成されます。そのURLをこのiPadのホーム画面に",
  "dev.3": "「App Icon」として追加してください。以降ゲストは施設コードを入力する必要がなく、",
  "dev.4": "チェックイン日を入力するだけで次の画面に進めます。",
  "dev.5": "読み込み中…",
  "dev.6": "施設コード:",
  "dev.7": "施設コード（接頭辞）が設定された有効な施設がありません。",
  "dev.8": "「施設管理」で各物件に接頭辞（例: SD）を設定してください。",
  "dev.9": "次の施設は施設コードが未設定のため選べません（施設管理で設定が必要）:",
  "dev.10": "、",
  "dev.11": "をこのiPadに設定",
  "dev.12": "下のURLをこのiPadのホーム画面に追加してください。",
  "dev.13": "このURLを開く",
  "dev.14": "コピーしました ✓",
  "dev.15": "URLをコピー",
  "dev.16": "ホーム画面への追加手順（iPad / Safari）",
  "dev.17": "「このURLを開く」でSafariに表示します。",
  "dev.18": "共有ボタン（□↑）→「ホーム画面に追加」を選びます。",
  "dev.19": "名前を付けて「追加」。以降はそのアイコンから起動します。",
  "au.0": "エラーが発生しました ({p0})",
  "au.1": "「{p0}」を無効にします。この人はすぐにログアウトされ、ログインできなくなります。よろしいですか？",
  "au.2": "「{p0}」を有効にしました",
  "au.3": "「{p0}」を無効にしました",
  "au.4": "管理者アカウント",
  "au.5": "＋ 管理者を追加",
  "au.6": "読み込み中…",
  "au.7": "表示名",
  "au.8": "ログインID",
  "au.9": "状態",
  "au.10": "最終ログイン",
  "au.11": "操作",
  "au.12": "あなた",
  "au.13": "有効",
  "au.14": "無効",
  "au.15": "名前変更",
  "au.16": "パスワード変更",
  "au.17": "無効にする",
  "au.18": "有効にする",
  "au.19": "管理者アカウントがありません",
  "au.20": "※ アカウントは削除せず「無効」にします。無効にした人は、すぐにログアウトされます。",
  "au.21": "パスワードを変更しました。新しいパスワードでログインし直してください。",
  "au.22": "閉じる",
  "au.23": "キャンセル",
  "au.24": "保存中…",
  "au.25": "パスワードは{p0}文字以上にしてください",
  "au.26": "パスワードが一致しません",
  "au.27": "「{p0}」を追加しました",
  "au.28": "管理者を追加",
  "au.29": "追加",
  "au.30": "ログインID（英数字と . _ @ -、3〜64文字）",
  "au.31": "パスワード（",
  "au.32": "文字以上）",
  "au.33": "パスワード（確認）",
  "au.34": "表示名を変更しました",
  "au.35": "表示名の変更（{p0}）",
  "au.36": "保存",
  "au.37": "「{p0}」のパスワードを変更しました",
  "au.38": "パスワード変更（{p0}）",
  "au.39": "変更",
  "au.40": "現在のパスワード",
  "au.41": "新しいパスワード（",
  "au.42": "新しいパスワード（確認）",
  "au.43": "変更すると、あなたを含めこのアカウントのログインはすべて切れます。",
  "au.44": "変更すると、この人はすぐにログアウトされます。",
  "mt.0": "当日",
  "mt.1": "前日",
  "mt.2": "{p0}日前",
  "mt.3": "予約確定後",
  "mt.4": "チェックイン基準",
  "mt.5": "チェックアウト基準",
  "mt.6": "予約確定",
  "mt.7": "決済完了・入金確認後に送信",
  "mt.8": "振込案内",
  "mt.9": "銀行振込を選んだ予約の作成時に送信",
  "mt.10": "支払いリマインダー",
  "mt.11": "振込期限の約12時間前に送信",
  "mt.12": "キャンセル承認",
  "mt.13": "キャンセル承認時に送信",
  "mt.14": "チェックイン案内",
  "mt.15": "チェックイン前日 10:00 以降に送信",
  "mt.16": "チェックアウト案内",
  "mt.17": "チェックアウト前日 18:00 以降に送信",
  "mt.18": "パスポート案内",
  "mt.19": "自社予約ゲストへアップロードURLを自動送信",
  "mt.22": "ゲスト名",
  "mt.23": "予約番号",
  "mt.24": "チェックイン日",
  "mt.25": "チェックアウト日",
  "mt.26": "チェックイン時刻",
  "mt.27": "チェックアウト時刻",
  "mt.28": "人数",
  "mt.29": "合計金額",
  "mt.30": "物件名",
  "mt.31": "物件URL",
  "mt.32": "施設名",
  "mt.33": "施設メール",
  "mt.34": "無料キャンセル期限",
  "mt.35": "振込期限(時間)",
  "mt.36": "銀行名",
  "mt.37": "支店名",
  "mt.38": "口座種別",
  "mt.39": "口座番号",
  "mt.40": "口座名義",
  "mt.41": "返金文言(キャンセル)",
  "mt.42": "パスポートURL",
  "mt.43": "読み込みに失敗しました ({p0})",
  "mt.44": "保存に失敗しました ({p0})",
  "mt.45": "送信タイミングを保存しました。次回の定期処理（毎時0分）から反映されます。",
  "mt.46": "自動送信オフ",
  "mt.47": "予約確定の{p0}分後に送信",
  "mt.48": "チェックイン",
  "mt.49": "チェックアウト",
  "mt.50": "{p0}{p1} {p2}:00 以降に送信",
  "mt.51": "振込期限の{p0}時間前に送信",
  "mt.52": "保存していない変更があります。破棄して切り替えますか？",
  "mt.53": "保存しました。次回送信分から反映されます。",
  "mt.54": "テスト送信に失敗しました ({p0})",
  "mt.55": "{p0} にテストメールを送信しました。",
  "mt.56": "メールテンプレート",
  "mt.57": "ゲストへ自動送信されるメールの件名・本文を編集できます。",
  "mt.58": "読み込み中…",
  "mt.59": "未作成",
  "mt.60": "送信タイミング",
  "mt.61": "自動送信する",
  "mt.62": "基準イベント",
  "mt.63": "予約確定の",
  "mt.64": "直後",
  "mt.65": "{p0}時間後",
  "mt.66": "{p0}分後",
  "mt.67": "に送信",
  "mt.68": "の",
  "mt.69": "以降に送信",
  "mt.70": "※定期処理は毎時0分に動くため、時刻は「時」単位です（「予約確定後N分」も次の毎時実行で送信されます）。設定は全言語・全物件共通です。",
  "mt.71": " パスポート案内の自動送信は自社予約のみが対象です（OTA予約はメッセージ機能で手動共有）。",
  "mt.72": "元に戻す",
  "mt.73": "保存中…",
  "mt.74": "タイミングを保存",
  "mt.75": "振込期限の",
  "mt.76": "時間前",
  "mt.77": "（振込期限は予約から",
  "mt.78": "時間）",
  "mt.79": "※定期処理は毎時0分に動くため、時刻は「時」単位です。設定は全言語・全物件共通です。",
  "mt.80": " 予約が送信時刻より後に確定した場合は、確定後の最初の定期処理で送信します。",
  "mt.81": "件名",
  "mt.82": "本文",
  "mt.83": "書き方のコツ：",
  "mt.85": "や",
  "mt.87": "は見出しに、",
  "mt.89": "が2行以上続くと表になります。",
  "mt.90": "は区切り線です。",
  "mt.91": "差し込み項目（クリックで挿入）",
  "mt.92": "テスト送信（編集中の文面・サンプル予約で送信／保存は不要）",
  "mt.93": "送信中…",
  "mt.94": "テスト送信",
  "mt.95": "最終更新:",
  "mt.96": "保存",
  "mt.97": "プレビュー（サンプル予約）",
  "mt.98": "スマホ",
  "mt.99": "件名:",
};
export type AdminKey = keyof typeof ja;

const zh: Record<AdminKey, string> = {
  'clnCk.result': '检查清单（已确认 {n} 项）', 'clnCk.noResult': '没有检查清单记录（引入检查清单之前的报告，或由管理员手动完成）', 'clnCk.checkedBy': '报告人: {s}　{t}',
  'clnCk.tab': '检查清单', 'clnCk.title': '清扫检查清单', 'clnCk.desc': '清洁人员提交完成报告时，必须勾选此处登记的全部项目。除全部房源通用项目外，还可为各房源追加项目。',
  'clnCk.scope': '适用范围', 'clnCk.common': '全部房源通用', 'clnCk.inherit': '该房源还会显示全部房源通用的 {n} 个项目。', 'clnCk.empty': '没有项目',
  'clnCk.inactive': '已停用', 'clnCk.save': '保存', 'clnCk.cancel': '取消', 'clnCk.edit': '编辑', 'clnCk.disable': '停用', 'clnCk.enable': '启用',
  'clnCk.del': '删除', 'clnCk.confirmDel': '要删除「{s}」吗？（过去的报告记录会保留）', 'clnCk.ph': '例：清除浴室排水口的头发', 'clnCk.add': '添加',
  loading: '加载中...', menu: '菜单', logout: '退出登录', language: '语言', legacyUser: '共享密码（过渡期）',
  'nav.dashboard': '仪表盘', 'nav.calendar': '日历', 'nav.cleaning': '清洁管理', 'nav.reservations': '订单管理',
  'nav.messages': '客人联系', 'nav.properties': '房源管理', 'nav.passport': '护照管理', 'nav.checkin': '入住身份确认',
  'nav.settings': '设施设置', 'nav.adminUsers': '管理员账号', 'nav.otaReview': 'OTA同步确认', 'rsv.otaIncomplete': '待补充预订信息', 'rsv.otaCancelSuspected': '疑似取消', 'rsv.csConfidence': '可信度', 'rsv.csStreak': '连续{p0}次未检测到', 'rsv.cs_HIGH': '高', 'rsv.cs_MEDIUM': '中', 'rsv.cs_LOW': '低', 'rsv.cs_UID_MISSING': '事件已从OTA日历中消失', 'rsv.cs_NO_COVERAGE': 'OTA日历中没有该日期的事件', 'rsv.cs_PARTIAL_COVERAGE': '与OTA日期仅部分一致', 'rsv.cs_SOURCE_BLOCK_REMOVED': '来源的Airbnb封锁已消失', 'rsv.cs_FEED_EMPTIED': 'OTA日历中没有今后的事件', 'rsv.cs_DATE_MISMATCH': '与OTA日历的日期不一致', 'rsv.otaActiveInFeed': 'OTA上有效', 'rsv.cs_ACTIVE_IN_FEED': '已取消，但在OTA上仍是有效预订',
  'rsv.otaReview': '需确认（OTA）', 'rsv.otaDateChanged': '日期已变更', 'rsv.otaMissing': '已从OTA日历中消失', 'rsv.otaOverlap': '与其他预订日期重叠', 'rsv.otaUnset': '未填写', 'rsv.otaCode': 'OTA预订号', 'rsv.otaGoReview': '前往OTA同步确认处理',
  'login.title': '管理后台登录', 'login.id': '登录ID', 'login.pw': '密码', 'login.submit': '登录', 'login.checking': '验证中...',
  'login.noServer': '无法连接API服务器', 'login.failed': '登录失败',
  'db.attention': '⚠ 有 {n} 个订单需要处理',
  'db.attentionText': '这些订单在预留过期后才付款，未能确保日期。请重新确保日期或办理退款。',
  'db.viewList': '查看列表 →', 'db.total': '全部订单', 'db.newWeek': '一周内新订单', 'db.newWeekSub': '过去7天收到的订单',
  'db.pending': '待确认', 'db.availTitle': '今天至下月底的空房天数', 'db.availTip': '{name}（空{v}/{t}天）', 'db.days': '天', 'db.daysUnit': '天',
  'db.noProps': '没有房源', 'db.checkin': '入住', 'db.checkout': '退房', 'db.staying': '住宿中', 'db.vacant': '空房',
  'db.cleaning': '清洁', 'db.cleaningErr': '无法获取清洁信息', 'db.cleanToday': '今日清洁',
  'db.cleanTodaySub': '已完成 {d} ／ 剩余 {r}', 'db.cleanTomorrow': '明日清洁', 'db.openCleaning': '打开清洁管理 →',
  'db.unassigned': '本周未分配', 'db.unassignedSub': '今天起7天・无负责人',
  'db.upcoming': '一周内的入住・退房', 'db.noUpcoming': '今天起一周内没有安排',
  'db.colProp': '房源', 'db.colGuests': '人数', 'db.guestsN': '{n}人', 'db.colPassport': '护照登记', 'db.colCheckinSt': '入住状态',
  'db.registered': '已登记', 'db.notRegistered': '未登记', 'db.csNone': '未', 'db.csProgress': '办理中', 'db.csDone': '完成',
  "cln.stPending": "🧹 未开始",
  "cln.stProgress": "🔄 进行中",
  "cln.stDone": "✅ 已完成",
  "cln.stReview": "⚠️ 需确认",
  "cln.stCancelled": "已取消",
  "cln.err": "发生错误 ({s})",
  "clnA.0": "未分配",
  "clnD.0": "加载中…",
  "clnD.1": "今日概况",
  "clnD.2": "↻ 刷新",
  "clnD.3": "今日清洁",
  "clnD.4": "项",
  "clnD.5": "已完成",
  "clnD.6": "／ 剩余",
  "clnD.7": "逾期未完成",
  "clnD.8": "昨天及以前未开始・进行中",
  "clnD.9": "需确认",
  "clnD.10": "需要管理员判断",
  "clnD.11": "本周未分配",
  "clnD.12": "今天起7天",
  "clnD.13": "明日清洁",
  "clnD.14": "⚠ 逾期未完成",
  "clnD.15": "⚠️ 需确认",
  "clnD.16": "未来一周的清洁任务（{p0}〜{p1}）",
  "clnD.17": "未来一周没有清洁安排。",
  "clnD.18": "※ 点击一行即可在日历中打开当天详情（可在那里修改状态和负责人）。",
  "clnD.19": "清洁日期",
  "clnD.20": "房源",
  "clnD.21": "负责人",
  "clnD.22": "计划时间",
  "clnD.23": "状态",
  "clnT.0": "本周",
  "clnT.1": "下周",
  "clnT.2": "上周",
  "clnT.3": "本月",
  "clnT.4": "上月",
  "clnT.5": "指定期间",
  "clnT.6": "请正确指定期间。",
  "clnT.7": "期间",
  "clnT.8": "开始日期",
  "clnT.10": "结束日期",
  "clnT.11": "房源",
  "clnT.12": "全部房源",
  "clnT.13": "负责人",
  "clnT.14": "全部负责人",
  "clnT.15": "仅未分配",
  "clnT.16": "（已停用）",
  "clnT.17": "状态",
  "clnT.18": "已完成和需确认",
  "clnT.19": "仅已完成",
  "clnT.20": "仅需确认",
  "clnT.21": "全部未完成",
  "clnT.22": "未开始",
  "clnT.23": "进行中",
  "clnT.24": "需确认",
  "clnT.25": "：",
  "clnT.26": "项",
  "clnT.27": "／ 备品费合计",
  "clnT.28": "日元",
  "clnT.29": "（最多显示500项，请缩小期间）",
  "clnT.30": "加载中…",
  "clnT.31": "没有符合条件的清洁任务。",
  "clnT.32": "清洁日期",
  "clnT.33": "开始",
  "clnT.34": "结束",
  "clnT.35": "结果",
  "clnT.36": "备品费",
  "clnT.37": "审批",
  "clnT.38": "{p0}日元",
  "clnT.39": "确认",
  "clnT.40": "去确认",
  "clnT.41": "详情",
  "clnT.42": "计划时间",
  "clnT.43": "※ 点击一行即可在日历中打开当天详情。",
  "clnT.44": "✔ 已批准",
  "clnT.45": "✖ 退回中",
  "clnT.46": "未审批",
  "clnT.47": "✔ 批准",
  "clnT.48": "✖ 驳回（退回）",
  "clnT.49": "💬 留言",
  "clnT.50": "驳回时请填写留言（需要重做的内容）。",
  "clnT.51": "请填写留言。",
  "clnT.52": "（已通知{p0}位负责人）",
  "clnT.53": "（未分配负责人，未发送通知）",
  "clnT.54": "已批准{p0}",
  "clnT.55": "已驳回并退回{p0}",
  "clnT.56": "已发送留言{p0}",
  "clnT.57": "确认清洁结果",
  "clnT.58": "工作时间",
  "clnT.59": "需确认原因",
  "clnT.60": "联系备注",
  "clnT.61": "备品购买费用",
  "clnT.62": "管理员备注",
  "clnT.63": "往来记录",
  "clnT.64": "管理员",
  "clnT.65": "清洁人员",
  "clnT.66": "・",
  "clnT.67": "留言（会通知清洁负责人。",
  "clnT.68": "驳回时必填",
  "clnT.69": "）",
  "clnT.70": "例：浴室镜子上有水垢，请重新擦拭。",
  "clnT.71": "给负责人的联系事项",
  "clnT.72": "※ 只有“已完成”的清洁可以批准或驳回。留言可以随时发送。",
  "clnT.73": "要驳回并退回吗？",
  "clnT.74": "状态将变回“进行中”，负责人重做后再次提交完成报告。结束时间的记录会被删除。",
  "clnT.75": "取消",
  "clnT.76": "处理中…",
  "clnT.77": "驳回并退回",
  "clnT.78": "在日历中打开",
  "clnT.79": "💬 仅发送留言",
  "clnT.80": "✖ 驳回",
  "clnT.81": "✔ 已批准",
  "clnT.82": "关闭",
  "clnT.83": "照片（{p0}张）",
  "clnT.84": "清扫前",
  "clnT.85": "清扫后",
  "clnT.86": "遗留物品",
  "clnT.87": "损坏・污渍",
  "clnT.88": "其他",
  "clnT.89": "照片 {p0}张",
  "clnM.0": "发生错误 ({p0})",
  "clnM.1": "密码至少需要8个字符",
  "clnM.2": "两次输入的密码不一致",
  "clnM.3": "已为“{p0}”设置登录。登录网址：{p1}/cleaner",
  "clnM.4": "已保存“{p0}”",
  "clnM.5": "（已分配到 {p0} 项无负责人的清洁任务）",
  "clnM.6": "将停用“{p0}”。\n此人将从今天以后未开始的清洁任务中移除（过去的工作记录会保留）。确定吗？",
  "clnM.7": "已启用“{p0}”",
  "clnM.8": "（已分配到 {p0} 项清洁任务）",
  "clnM.9": "已停用“{p0}”",
  "clnM.10": "（已从 {p0} 项清洁任务中移除）",
  "clnM.11": "按房源筛选",
  "clnM.12": "全部房源",
  "clnM.13": "＋ 添加负责人",
  "clnM.14": "加载中…",
  "clnM.15": "还没有登记负责人。",
  "clnM.16": "此房源没有负责人。",
  "clnM.17": "已停用",
  "clnM.18": "计划",
  "clnM.19": "项 ／ 已完成",
  "clnM.20": "项",
  "clnM.21": "📍 希望区域：",
  "clnM.22": "🔑 登录ID：",
  "clnM.23": "🔑 未设置登录",
  "clnM.24": "负责房源：",
  "clnM.25": "无",
  "clnM.26": "编辑",
  "clnM.27": "重新设置登录",
  "clnM.28": "设置登录",
  "clnM.29": "停用",
  "clnM.30": "启用",
  "clnM.31": "※ 负责人不会被删除，只会“停用”。分配房源后，会自动加入该房源今天以后无负责人的清洁任务。",
  "clnM.32": "设置清洁人员登录（",
  "clnM.33": "）",
  "clnM.34": "登录ID（字母数字和 . _ @ -，3〜64个字符）",
  "clnM.35": "新密码（至少8个字符）",
  "clnM.36": "新密码（确认）",
  "clnM.37": "※ 登录网址：",
  "clnM.38": "。请把ID和密码告诉负责人。重新设置后，此人的登录会被退出一次。",
  "clnM.39": "取消",
  "clnM.40": "保存中…",
  "clnM.41": "保存",
  "clnM.42": "添加负责人",
  "clnM.43": "编辑负责人",
  "clnM.44": "姓名 *",
  "clnM.45": "电话",
  "clnM.46": "邮箱",
  "clnM.47": "希望负责区域",
  "clnM.48": "例：新宿区・中野区",
  "clnM.49": "地址（选填）",
  "clnM.50": "负责房源",
  "clnP.0": "自营",
  "clnP.1": "手动添加",
  "clnP.2": "🧹 未开始",
  "clnP.3": "🔄 进行中",
  "clnP.4": "✅ 已完成",
  "clnP.5": "⚠️ 需确认",
  "clnP.6": "已取消",
  "clnP.7": "获取清洁安排失败。",
  "clnP.8": "更改负责人失败。",
  "clnP.9": "操作失败。",
  "clnP.10": "请输入标为需确认的原因（例：工作中途停止、备品损坏等）",
  "clnP.11": "请输入原因。",
  "clnP.12": "结束时间必须晚于开始时间",
  "clnP.13": "保存失败。",
  "clnP.14": "添加清洁日期失败。",
  "clnP.15": "清洁管理",
  "clnP.16": "＋ 添加清洁日期",
  "clnP.17": "🏠 仪表盘",
  "clnP.18": "📅 日历",
  "clnP.19": "📋 任务列表",
  "clnP.20": "✅ 完成列表",
  "clnP.21": "👤 负责人",
  "clnP.22": "🛎️ 入住",
  "clnP.23": "上个月",
  "clnP.24": "‹ 上个月",
  "clnP.25": "年",
  "clnP.26": "月",
  "clnP.27": "下个月",
  "clnP.28": "下个月 ›",
  "clnP.29": "加载中…",
  "clnP.30": "{p0}（{p1}）\n清洁日期（退房）：{p2}",
  "clnP.31": "{p0}（{p1}）\n入住：{p2}",
  "clnP.32": " 的清洁安排",
  "clnP.33": "当天没有清洁安排。",
  "clnP.34": "（",
  "clnP.35": "）",
  "clnP.36": "清洁日期（退房）：",
  "clnP.37": "计划：",
  "clnP.39": "未设置",
  "clnP.40": "／ 入住：",
  "clnP.41": "实际：",
  "clnP.42": "⚠️ 需确认原因：",
  "clnP.43": "备注：",
  "clnP.44": "负责人：",
  "clnP.45": "（已停用）",
  "clnP.46": "未分配",
  "clnP.47": "开始工作",
  "clnP.48": "完成",
  "clnP.49": "需确认",
  "clnP.50": "改回未开始",
  "clnP.51": "负责人",
  "clnP.52": "计划时间・备注",
  "clnP.53": "要取消这项清洁安排吗？",
  "clnP.54": "查看订单",
  "clnP.55": "🛎️ 当天入住（",
  "clnP.56": "项）",
  "clnP.57": "退房（清洁日期）：",
  "clnP.58": "更改负责人",
  "clnP.59": "／",
  "clnP.60": "没有登记负责人。请在“负责人”标签页中添加。",
  "clnP.61": "此房源负责人",
  "clnP.62": "已停用",
  "clnP.63": "※ 分配多人时，只要其中一人提交完成报告即视为完成。",
  "clnP.64": "取消",
  "clnP.65": "保存",
  "clnP.66": "计划开始",
  "clnP.67": "计划结束",
  "clnP.68": "填入房源时间（",
  "clnP.69": "清除时间",
  "clnP.70": "备注（清洁人员也能看到）",
  "clnP.71": "保存中…",
  "clnP.72": "添加清洁日期",
  "clnP.73": "与订单无关，手动添加1项清洁安排（同一天已有安排时无法添加）。",
  "clnP.74": "房源",
  "clnP.75": "请选择",
  "clnP.76": "清洁日期",
  "clnP.77": "计划开始（选填）",
  "clnP.78": "计划结束（选填）",
  "clnP.79": "备注（选填）",
  "clnP.80": "添加",
  "rsv.0": "订单已批准",
  "rsv.1": "订单已确定",
  "rsv.2": "取消申请",
  "rsv.3": "已批准取消",
  "rsv.4": "已收到护照",
  "rsv.5": "今日入住",
  "rsv.6": "住宿中",
  "rsv.7": "今日退房",
  "rsv.8": "已结束",
  "rsv.9": "登记OTA订单失败。",
  "rsv.10": "OTA订单已登记。请通过OTA的消息功能，把下面的“固定网址・预订号・入住日期”发给客人。",
  "rsv.11": "管理员认证失败（401）。请检查 ADMIN_PASSWORD_HASH 的设置。",
  "rsv.12": "获取订单列表失败（{p0}）{p1}",
  "rsv.13": "无法连接API服务器。请确认API是否已启动。",
  "rsv.14": "获取订单列表失败",
  "rsv.15": "操作失败 ({p0})",
  "rsv.16": "已取消，并通过Stripe全额退款。",
  "rsv.17": "已取消。{p0}",
  "rsv.18": "\n已向客人发送取消完成邮件。",
  "rsv.19": "\n※向客人发送邮件失败。请从客人联系页面联系。",
  "rsv.20": "更新失败 ({p0})",
  "rsv.21": "待付款",
  "rsv.22": "待转账",
  "rsv.23": "已确定",
  "rsv.24": "已取消",
  "rsv.25": "已过期",
  "rsv.26": "已退款",
  "rsv.27": "⚠ 需处理（过期后付款）",
  "rsv.28": "⇩ 从Booking导入",
  "rsv.29": "⇩ 从Airbnb导入",
  "rsv.30": "关闭",
  "rsv.31": "＋ 登记OTA订单",
  "rsv.32": "重新加载",
  "rsv.33": "全部",
  "rsv.34": "按房源筛选",
  "rsv.35": "全部房源",
  "rsv.36": "排序",
  "rsv.37": "最新预订",
  "rsv.38": "入住日期（升序）",
  "rsv.39": "入住日期（降序）",
  "rsv.40": "显示筛选",
  "rsv.41": "显示筛选：",
  "rsv.42": "也显示已过退房日期的订单",
  "rsv.43": "也显示过去的订单",
  "rsv.44": "也显示已取消・已退款的订单",
  "rsv.45": "也显示已取消",
  "rsv.46": "按状态筛选（可多选）",
  "rsv.47": "状态：",
  "rsv.48": "清除",
  "rsv.49": "房源",
  "rsv.50": "点击切换升序・降序",
  "rsv.51": "入住",
  "rsv.52": "退房",
  "rsv.53": "人数",
  "rsv.54": "免费取消期限",
  "rsv.55": "状态",
  "rsv.56": "操作",
  "rsv.57": "{p0}人",
  "rsv.58": "详情",
  "rsv.59": "没有订单",
  "rsv.60": "订单详情",
  "rsv.61": "预订号",
  "rsv.62": "客人姓名",
  "rsv.63": "邮箱",
  "rsv.64": "金额",
  "rsv.65": "付款状态：",
  "rsv.66": "手动设置",
  "rsv.67": "有取消申请",
  "rsv.68": "申请时间：",
  "rsv.69": "（UTC）",
  "rsv.70": "原因：",
  "rsv.71": "前可免费取消（入住前",
  "rsv.72": "天）",
  "rsv.73": "之后不可退款",
  "rsv.74": "确认此订单的付款并设为已确定吗？（会检查Stripe是否已付款）",
  "rsv.75": "确认付款并确定",
  "rsv.76": "重新确保日期并确定订单吗？（如无空房则无法确定）",
  "rsv.77": "重新确保日期并确定",
  "rsv.78": "要通过Stripe全额退款吗？（无法撤销）",
  "rsv.79": "全额退款",
  "rsv.80": "确认此订单的到账并设为已确定吗？",
  "rsv.81": "确认到账并确定",
  "rsv.82": "在免费取消期限内，取消的同时将通过Stripe全额退款（无法撤销）。\n确定吗？",
  "rsv.83": "要取消此订单吗？（会释放库存，不退款）",
  "rsv.84": "批准取消",
  "rsv.85": "取消订单",
  "rsv.86": "联系客人",
  "ota.0": "其他OTA",
  "ota.1": "房源",
  "ota.2": "请选择",
  "ota.3": "入住",
  "ota.4": "退房",
  "ota.5": "入住人数",
  "ota.6": "客人姓名（选填・OTA显示名）",
  "ota.7": "OTA预订号（选填）",
  "ota.8": "登记并发放网址",
  "iss.0": "固定网址",
  "iss.1": "上传网址",
  "iss.2": "已复制",
  "iss.3": "复制",
  "iss.4": "含预订号的网址",
  "iss.5": "预订号",
  "iss.6": "入住日期：",
  "iss.7": "※ 请直接把这个网址交给客人（邮件或OTA消息等）。旧网址已失效，这是唯一有效的网址。",
  "iss.8": "关闭",
  "iss.9": "收件人：",
  "iss.10": "主题",
  "iss.11": "正文（模板・可编辑）",
  "iss.12": "请确认内容后发送",
  "iss.13": "发送中…",
  "iss.14": "发送邮件",
  "iss.15": "※ OTA订单无需发送邮件。请复制上面的内容，通过OTA网站的消息联系客人。",
  "abi.0": "确认码",
  "abi.8": "未找到数据行。",
  "abi.9": "没有可导入的订单行。",
  "abi.10": "已读取 {p0} 行订单（房源代码：{p1}）。请确认内容后按“导入”。",
  "abi.11": "读取文件失败：{p0}",
  "abi.12": "请选择文件。",
  "abi.13": "导入失败。",
  "abi.14": "从 Airbnb 导入订单",
  "abi.15": "请选择 Airbnb 的订单CSV。文件在浏览器内解析。",
  "abi.16": "房源根据 CSV 的“リスティング”列（SD / FT / GW 等房源代码）自动判别，无需选择房源。",
  "abi.17": "不导入金额（仅订单数据）。已登记的确认码不会重复登记。",
  "abi.18": "文件（Airbnb 订单CSV）",
  "abi.19": "已登记",
  "abi.20": "跳过",
  "abi.21": "错误",
  "abi.22": "合计",
  "abi.23": "行",
  "abi.24": "房源",
  "abi.25": "结果",
  "abi.26": "原因",
  "abi.27": "关闭",
  "abi.28": "取消",
  "abi.29": "导入中…",
  "abi.30": "导入{p0}",
  "bki.0": "预订号",
  "bki.9": "未找到数据行。",
  "bki.10": "找不到必要的列：{p0}。请确认是否为Booking.com的订单列表文件。",
  "bki.11": "没有可导入的订单行。",
  "bki.12": "已读取 {p0} 行订单。请确认内容后按“导入”。",
  "bki.13": "读取文件失败：{p0}",
  "bki.14": "请选择房源。",
  "bki.15": "请选择文件。",
  "bki.16": "导入失败。",
  "bki.17": "从 Booking.com 导入订单",
  "bki.18": "请选择房源，并选择 Booking.com 的订单列表文件（.xls / .xlsx / .csv）。",
  "bki.19": "文件在浏览器内解析，每个订单都会登记为该房源的订单（视为已确定・自动生成预订号）。",
  "bki.20": "已登记的预订号不会重复登记。",
  "bki.21": "房源",
  "bki.22": "请选择",
  "bki.23": "文件",
  "bki.24": "已登记",
  "bki.25": "跳过",
  "bki.26": "错误",
  "bki.27": "合计",
  "bki.28": "行",
  "bki.29": "结果",
  "bki.30": "原因",
  "bki.31": "关闭",
  "bki.32": "取消",
  "bki.33": "导入中…",
  "bki.34": "导入{p0}",
  "abi.count": "（{n}项）",
  "bki.count": "（{n}项）",
  "cal.0": "手动封锁",
  "cal.1": "{p0}年 {p1}月",
  "cal.2": "同步失败",
  "cal.3": "同步完成：已导入 {p0} 晚",
  "cal.4": "错误：{p0}",
  "cal.5": "上个月",
  "cal.6": "下个月",
  "cal.7": "本月",
  "cal.8": "请在标签中选择要同步的房源",
  "cal.9": "立即同步此房源的OTA日历",
  "cal.10": "同步中…",
  "cal.11": "立即同步此房源",
  "cal.12": "全部房源",
  "cal.13": "已确定",
  "cal.14": "待转账",
  "cal.15": "Stop（Airbnb/OTA导入）",
  "cal.16": "｜ OTA Stop：",
  "cal.18": "项",
  "cal.19": "{p0} / 因 {p1} 而Stop（不可预订）",
  "cal.20": "■ Stop（",
  "cal.21": "）",
  "pp.0": "男",
  "pp.1": "女",
  "pp.2": "其他",
  "pp.3": "自营",
  "pp.4": "其他OTA",
  "pp.5": "无法获取文件。",
  "pp.6": "获取名册CSV失败。",
  "pp.7": "将删除此文件，无法恢复。确定吗？",
  "pp.8": "将删除此客人的全部登记信息和提交的图片，恢复为未登记状态。\n无法恢复。确定吗？",
  "pp.9": "已删除客人的登记信息。",
  "pp.10": "删除客人信息失败。",
  "pp.11": "发放网址失败。",
  "pp.12": "这是OTA订单。请复制下面的“固定网址・预订号・入住日期”，通过OTA网站的消息联系客人。",
  "pp.13": "网址已发放。请确认邮件内容，没有问题就按“发送邮件”。",
  "pp.14": "发放即时网址后，此订单现有的网址会立即失效，\n",
  "pp.15": "只有新发放的网址有效。\n",
  "pp.16": "如果已经把旧网址告诉了客人，旧网址将无法使用。\n\n",
  "pp.17": "要继续吗？",
  "pp.18": "发放补救网址失败。",
  "pp.19": "已显示有效的固定网址・预订号。请通过OTA网站的消息交给客人。",
  "pp.20": "已发放1个有效的上传网址。请直接交给客人（旧网址已失效）。",
  "pp.21": "发送邮件失败。",
  "pp.22": "（已跳过发送邮件：未配置发送设置）",
  "pp.23": "已通过邮件发送上传网址。",
  "pp.24": "登记OTA订单失败。",
  "pp.25": "OTA订单已登记。请通过OTA的消息功能，把下面的“固定网址・预订号・入住日期”发给客人。",
  "pp.26": "护照管理",
  "pp.27": "关闭",
  "pp.28": "＋ 登记OTA订单",
  "pp.29": "网址发放结果",
  "pp.30": "房源",
  "pp.31": "全部房源",
  "pp.32": "也显示过去的入住日期",
  "pp.33": "项",
  "pp.34": "加载中…",
  "pp.35": "房源・预订号",
  "pp.36": "类别",
  "pp.37": "点击切换升序/降序",
  "pp.38": "入住",
  "pp.39": "登记状况",
  "pp.40": "期限",
  "pp.41": "没有符合的订单",
  "pp.42": "完成",
  "pp.43": "未完成",
  "pp.44": "已取消",
  "pp.45": "已过期",
  "pp.46": "有效",
  "pp.47": "操作",
  "pp.48": "详情",
  "pp.49": "发放网址",
  "pp.50": "重新生成1个有效网址并直接交给客人",
  "pp.51": "即时发放网址",
  "pp.52": "下载住宿者名册CSV",
  "pp.53": "名册CSV",
  "pp.54": "（",
  "pp.55": "）",
  "pp.56": "下载名册CSV",
  "pp.58": "客人",
  "pp.59": "：{p0}",
  "pp.60": "（未登记）",
  "pp.61": "已登记",
  "pp.62": "删除此客人的登记信息和图片，恢复为未登记",
  "pp.63": "重置登记",
  "pp.64": "出生日期：",
  "pp.65": "/ 国籍：",
  "pp.66": "地址：",
  "pp.67": "/ 目的地：",
  "pp.68": "无文件",
  "pp.69": "护照图片 #{p0}",
  "pp.70": "点击放大",
  "pp.71": "点击查看",
  "pp.72": "删除",
  "pp.73": "护照图片（放大）",
  "ci.0": "未提交",
  "ci.1": "已提交（待确认）",
  "ci.2": "确认中",
  "ci.3": "要求重新提交",
  "ci.4": "不一致",
  "ci.5": "确认完成",
  "ci.6": "需处理",
  "ci.7": "未开始",
  "ci.8": "完成",
  "ci.9": "男",
  "ci.10": "女",
  "ci.11": "其他",
  "ci.12": "此订单的房源未设置设施代码（预订号前缀）。请在房源管理中设置前缀（例：SD）。",
  "ci.13": "入住网址已发放。通常请把带日期的网址交给客人；如果只能填入OTA的固定网址栏，请交给客人设施固定网址。",
  "ci.14": "发送邮件失败。",
  "ci.15": "（已跳过发送邮件：未配置发送设置）",
  "ci.16": "已通过邮件发送上传网址。",
  "ci.17": "登记OTA订单失败。",
  "ci.18": "OTA订单已登记。请通过OTA的消息功能，把下面的“固定网址・预订号・入住日期”发给客人。",
  "ci.19": "无法获取订单信息。",
  "ci.20": "操作失败：{p0}",
  "ci.21": "要求重新提交",
  "ci.22": "记录为不一致",
  "ci.23": "记录为需处理",
  "ci.24": "点击放大",
  "ci.25": "加载中…",
  "ci.26": "入住 身份确认",
  "ci.27": "关闭",
  "ci.28": "＋ 登记OTA订单",
  "ci.29": "这是依法进行的身份确认。核对住宿者的",
  "ci.30": "面部照片",
  "ci.31": "与",
  "ci.32": "护照",
  "ci.33": "，并记录确认结果。",
  "ci.34": "入住网址",
  "ci.35": "入住网址（带日期）",
  "ci.36": "已复制",
  "ci.37": "复制",
  "ci.38": "设施固定网址（无日期）",
  "ci.39": "设施代码：",
  "ci.40": "／ 入住日期：",
  "ci.41": "※“带日期”的网址可让客人无需输入任何内容即可进入身份确认（面部照片・护照）。",
  "ci.42": "※“设施固定网址”不含日期，可直接填入OTA网站的固定网址栏（客人只需选择入住日期）。",
  "ci.43": "网址发放结果",
  "ci.44": "房源",
  "ci.45": "全部房源",
  "ci.46": "也显示过去的入住日期",
  "ci.47": "项",
  "ci.48": "点击切换升序/降序",
  "ci.49": "入住日期",
  "ci.50": "设施",
  "ci.51": "身份确认",
  "ci.52": "已确认",
  "ci.53": "没有符合的订单",
  "ci.54": "操作",
  "ci.55": "确认",
  "ci.56": "发放给客人的入住网址（含设施代码・日期）",
  "ci.57": "发放网址",
  "ci.58": "（",
  "ci.59": "）",
  "ci.61": "身份确认：",
  "ci.62": "住宿者",
  "ci.63": "：{p0}",
  "ci.64": "（未登记姓名）",
  "ci.65": "提交来源：",
  "ci.66": "设施平板",
  "ci.67": "客人设备",
  "ci.68": "出生日期：",
  "ci.69": "国籍：",
  "ci.70": "备注：",
  "ci.71": "身份确认 完成",
  "ci.72": "放大图片",
  "ci.73": "退回",
  "ci.74": "原因",
  "ci.75": "（选填）",
  "ci.76": "面部与护照不一致 等",
  "ci.77": "无法继续身份确认的原因 等",
  "ci.78": "图片不清晰 等",
  "ci.79": "取消",
  "ci.80": "记录",
  "adm.gender": "/ 性别：",
  "adm.passportNo": "/ 护照号码：",
  "adm.prevAcc": "/ 上一住宿地：",
  "msg.0": "待付款",
  "msg.1": "待转账",
  "msg.2": "已确定",
  "msg.3": "已取消",
  "msg.4": "已过期",
  "msg.5": "已退款",
  "msg.6": "需处理",
  "msg.7": "已发送",
  "msg.8": "未发送（无邮件设置）",
  "msg.9": "发送失败",
  "msg.10": "收到",
  "msg.11": "错误 ({p0})",
  "msg.12": "要删除与 {p0} 的消息记录（{p1} 条）吗？\n※订单数据不会被删除。无法恢复。",
  "msg.13": "删除失败",
  "msg.14": "无法连接API服务器",
  "msg.15": "获取失败",
  "msg.16": "客人联系",
  "msg.17": "重新加载",
  "msg.18": "按姓名・邮箱搜索",
  "msg.19": "按房源筛选",
  "msg.20": "全部房源",
  "msg.21": "排序",
  "msg.22": "最新往来",
  "msg.23": "入住日期（升序）",
  "msg.24": "入住日期（降序）",
  "msg.25": "项",
  "msg.26": "加载中...",
  "msg.27": "没有符合的客人",
  "msg.28": "客人 / 最后一条消息",
  "msg.29": "入住",
  "msg.30": "状态",
  "msg.31": "最后往来",
  "msg.32": "无订单",
  "msg.33": "(无姓名)",
  "msg.34": "自动",
  "msg.35": "发送",
  "msg.36": "还没有往来记录",
  "msg.37": " ・ {p0}",
  "msg.38": " ・ 订单 {p0} 项",
  "msg.40": "护照已发送",
  "msg.41": "不可取消",
  "msg.42": "菜单",
  "msg.43": "没有可删除的消息",
  "msg.44": "删除",
  "msg.45": "“{p0}”",
  "msg.46": "这条消息",
  "msg.47": "要删除{p0}吗？\n※无法恢复。",
  "msg.48": "已删除消息",
  "msg.49": "请填写主题",
  "msg.50": "请填写正文",
  "msg.51": "要向 {p0} 发送邮件吗？",
  "msg.52": "已记录收到的消息",
  "msg.53": "已记录，但没有邮件设置（RESEND_API_KEY），实际上未发送",
  "msg.54": "已发送",
  "msg.55": "发送失败",
  "msg.56": "更新护照状态失败",
  "msg.57": "← 返回列表",
  "msg.58": "在订单管理中打开",
  "msg.60": "收到护照后请勾选",
  "msg.61": "已收到护照",
  "msg.62": "还没有往来记录。可以从下面的表单发送第一条消息。",
  "msg.63": "客人",
  "msg.64": "自动邮件",
  "msg.65": "设施",
  "msg.66": "错误：",
  "msg.67": "⚠ 发件人认证失败（可能是冒充）。请仔细确认内容。",
  "msg.68": "发件人：",
  "msg.69": "发送邮件",
  "msg.70": "记录客人的回复",
  "msg.71": "主题",
  "msg.72": "主题（选填）",
  "msg.73": "相关订单",
  "msg.74": "输入消息（Ctrl+Enter 发送）",
  "msg.75": "粘贴客人发来的邮件正文",
  "msg.76": "处理中...",
  "msg.77": "记录",
  "prop.0": "启用",
  "prop.1": "停用",
  "prop.2": "最多人数：",
  "prop.3": "人",
  "prop.4": "基本人数：",
  "prop.5": "附加费用：",
  "prop.6": "清洁费：",
  "prop.7": "最少住宿晚数：",
  "prop.8": "晚",
  "prop.9": "入住：",
  "prop.10": "退房：",
  "prop.11": "吸烟：",
  "prop.12": "可吸烟",
  "prop.13": "禁烟",
  "prop.14": "派对：",
  "prop.15": "可",
  "prop.16": "不可",
  "prop.17": "免费取消：",
  "prop.18": "入住前",
  "prop.19": "天为止",
  "prop.20": "iCal 导出网址",
  "prop.21": "复制网址",
  "prop.22": "已复制",
  "prop.23": "复制",
  "prop.24": "未设置 — 在“编辑”中设置密钥后显示",
  "prop.25": "房源名称",
  "prop.26": "最多人数",
  "prop.27": "基本人数",
  "prop.28": "附加费用 (日元/人)",
  "prop.29": "清洁费 (日元)",
  "prop.30": "最少住宿晚数 (晚)",
  "prop.31": "退房",
  "prop.32": "Airbnb / OTA 日历导入网址 (iCal import)",
  "prop.33": "Airbnb等导出日历的ics网址。每15分钟导入一次并封锁库存。",
  "prop.icalBooking": "Booking.com iCal 网址",
  "prop.icalBookingHint": "Booking.com 导出日历的ics网址。预订会自动登记（客人信息待补充），Airbnb 上相同日期的封锁会自动视为重复封锁并确认。",
  "prop.34": "iCal 导出用密钥",
  "prop.35": "随机字符串（例：8f3a1c...）",
  "prop.36": "此房源的导出网址（在Airbnb的“导入其他日历”中粘贴）：",
  "prop.37": "设置密钥并保存后，会显示交给Airbnb的导出网址。",
  "prop.38": "重要规则・取消政策",
  "prop.39": "吸烟",
  "prop.40": "派对",
  "prop.41": "免费取消期限（入住前几天内免费）",
  "prop.42": "例：如为31，入住日31天前可免费取消，之后（30天以内）不退款。",
  "prop.43": "预订时会向客人明确具体的期限日期（入住日 − 此天数）。",
  "prop.44": "房源详情",
  "prop.45": "面积（㎡）",
  "prop.46": "卧室数量",
  "prop.47": "客厅数量",
  "prop.48": "楼层数",
  "prop.49": "双人床数",
  "prop.50": "单人床数",
  "prop.51": "被褥",
  "prop.52": "双人沙发床数",
  "prop.53": "单人沙发床数",
  "prop.54": "厕所数",
  "prop.55": "浴室／淋浴间数",
  "prop.56": "重点（每行一项。显示在首页卡片上）",
  "prop.57": "最多10人\n5间卧室\n屋顶阳台\n距车站步行3分钟",
  "prop.58": "每一项会带 ✔ 显示在卡片上。留空则不显示。",
  "prop.59": "“查看房源”链接网址",
  "prop.60": "首页房源卡片按钮的链接。留空则打开原来的房源页面（/property/房源ID）。",
  "prop.61": "OTA预订号前缀",
  "prop.62": "例：SD",
  "prop.63": "用于OTA（Airbnb/Booking.com等）订单预订号的房源前缀（例：Stay Dream → SD）。2〜4个字母数字。未设置的房源无法登记OTA订单。发放的预订号格式为“前缀-入住日期-随机6位”（例：SD-20261013-000742）。",
  "prop.64": "日历显示简称",
  "prop.65": "例：ステイ",
  "prop.66": "用于在 /admin/calendar・/admin/cleaning 日历中简短显示房源的简称（选填・可用日语）。未设置时显示房源代码（前缀 SD / FT 等）。",
  "set.0": "基本设置",
  "set.1": "邮件模板",
  "set.2": "入住接待终端",
  "set.3": "设施名称",
  "set.4": "例：ステイドリームホテル",
  "set.5": "用于邮件发件人名称和正文。",
  "set.6": "地址",
  "set.7": "例：东京都涩谷区〇〇1-2-3",
  "set.8": "电话号码",
  "set.9": "例：03-1234-5678",
  "set.10": "邮箱地址",
  "set.11": "例：info@staydreamhotel.com",
  "set.12": "用作邮件回复地址（Reply-To）和咨询联系方式。",
  "set.13": "银行名称",
  "set.14": "例：三菱UFJ银行",
  "set.15": "支行名称",
  "set.16": "例：涩谷支行",
  "set.17": "账户类型",
  "set.18": "例：普通",
  "set.19": "账号",
  "set.20": "例：1234567",
  "set.21": "账户名",
  "set.22": "例：カ）ステイドリーム",
  "set.23": "转账期限（小时）",
  "set.24": "例：72",
  "set.25": "从预订到转账期限的小时数。会填入转账通知邮件。",
  "set.26": "获取设置失败。",
  "set.27": "保存失败。",
  "set.28": "设施设置已保存。",
  "set.29": "设施设置",
  "set.30": "设置设施信息、转账账户等会填入预订确认和转账通知邮件的信息。邮件的发送时机可在“邮件模板”标签中设置。",
  "set.31": "加载中…",
  "set.32": "设施信息",
  "set.33": "转账账户信息",
  "set.34": "其他",
  "set.35": "恢复",
  "set.36": "保存中…",
  "set.37": "保存",
  "set.38": "管理画面标题",
  "set.39": "例：ステイドリームホテル 管理",
  "set.40": "显示在管理画面左上角的名称。留空则显示“ステイドリームホテル”。",
  "set.41": "预订网站标题",
  "set.42": "例：ステイドリームホテル",
  "set.43": "显示在预订网站（面向客人）左上角的名称。留空则显示“ステイドリームホテル”。",
  "cp.tab": "回头客优惠券",
  "rsv.coupon": "优惠券",
  "cp.desc": "向曾入住过的客人发送的全设施通用优惠券。有效代码始终只有1个，保存新代码后旧代码立即失效。折扣仅适用于住宿费（不含加人费、清洁费、消费税）。",
  "cp.status": "状态",
  "cp.st.NONE": "未登记",
  "cp.st.ACTIVE": "有效",
  "cp.st.PAUSED": "已暂停",
  "cp.st.NOT_STARTED": "未开始",
  "cp.st.EXPIRED": "已过期",
  "cp.code": "优惠券代码",
  "cp.codeHint": "字母、数字和连字符，3〜30个字符。不区分大小写（以大写保存）。",
  "cp.rate": "折扣率（%）",
  "cp.rateHint": "1〜{n} 的整数",
  "cp.from": "开始日期",
  "cp.until": "结束日期",
  "cp.periodHint": "按预订受理日（日本时间）判断。有效至结束日23:59。",
  "cp.note": "备注（内部）",
  "cp.updated": "最后更新：{t}（{by}）",
  "cp.usage": "使用此代码的预订：{n}件",
  "cp.save": "保存",
  "cp.saving": "保存中…",
  "cp.reset": "还原",
  "cp.pause": "暂停",
  "cp.resume": "恢复",
  "cp.confirmReplace": "更改优惠券代码后，当前代码（{s}）将立即无法使用。确定更改吗？",
  "cp.confirmPause": "将暂停优惠券（{s}）。新预订将无法使用（已确定的预订不变）。确定吗？",
  "cp.saved": "已保存",
  "cp.loadErr": "加载失败",
  "cp.saveErr": "保存失败",
  "cp.history": "历史代码",
  "cp.noHistory": "无",
  "cp.guestText": "客人通知文（复制用）",
  "cp.copy": "复制",
  "cp.copied": "已复制",
  "dev.0": "入住接待终端（iPad）设置",
  "dev.1": "这是放在现场的iPad的初始设置。选择设施后按“设置到此iPad”，",
  "dev.2": "就会生成入住接待用的网址。请把该网址添加到此iPad的主屏幕，",
  "dev.3": "作为“App图标”。之后客人无需输入设施代码，",
  "dev.4": "只需输入入住日期即可进入下一页。",
  "dev.5": "加载中…",
  "dev.6": "设施代码：",
  "dev.7": "没有设置了设施代码（前缀）的有效设施。",
  "dev.8": "请在“房源管理”中为各房源设置前缀（例：SD）。",
  "dev.9": "以下设施未设置设施代码，无法选择（需在房源管理中设置）：",
  "dev.10": "、",
  "dev.11": " 设置到此iPad",
  "dev.12": "请把下面的网址添加到此iPad的主屏幕。",
  "dev.13": "打开此网址",
  "dev.14": "已复制 ✓",
  "dev.15": "复制网址",
  "dev.16": "添加到主屏幕的步骤（iPad / Safari）",
  "dev.17": "用“打开此网址”在Safari中显示。",
  "dev.18": "点分享按钮（□↑）→ 选择“添加到主屏幕”。",
  "dev.19": "输入名称后点“添加”。之后从该图标启动。",
  "au.0": "发生错误 ({p0})",
  "au.1": "将停用“{p0}”。此人会立即被退出，并且无法登录。确定吗？",
  "au.2": "已启用“{p0}”",
  "au.3": "已停用“{p0}”",
  "au.4": "管理员账号",
  "au.5": "＋ 添加管理员",
  "au.6": "加载中…",
  "au.7": "显示名称",
  "au.8": "登录ID",
  "au.9": "状态",
  "au.10": "最后登录",
  "au.11": "操作",
  "au.12": "你",
  "au.13": "启用",
  "au.14": "停用",
  "au.15": "修改名称",
  "au.16": "修改密码",
  "au.17": "停用",
  "au.18": "启用",
  "au.19": "没有管理员账号",
  "au.20": "※ 账号不会删除，只会“停用”。被停用的人会立即被退出。",
  "au.21": "密码已修改。请用新密码重新登录。",
  "au.22": "关闭",
  "au.23": "取消",
  "au.24": "保存中…",
  "au.25": "密码至少需要{p0}个字符",
  "au.26": "两次输入的密码不一致",
  "au.27": "已添加“{p0}”",
  "au.28": "添加管理员",
  "au.29": "添加",
  "au.30": "登录ID（字母数字和 . _ @ -，3〜64个字符）",
  "au.31": "密码（至少",
  "au.32": "个字符）",
  "au.33": "密码（确认）",
  "au.34": "显示名称已修改",
  "au.35": "修改显示名称（{p0}）",
  "au.36": "保存",
  "au.37": "已修改“{p0}”的密码",
  "au.38": "修改密码（{p0}）",
  "au.39": "修改",
  "au.40": "当前密码",
  "au.41": "新密码（至少",
  "au.42": "新密码（确认）",
  "au.43": "修改后，包括你在内，此账号的所有登录都会被退出。",
  "au.44": "修改后，此人会立即被退出。",
  "mt.0": "当天",
  "mt.1": "前一天",
  "mt.2": "{p0}天前",
  "mt.3": "订单确定后",
  "mt.4": "以入住为基准",
  "mt.5": "以退房为基准",
  "mt.6": "订单确定",
  "mt.7": "付款完成・确认到账后发送",
  "mt.8": "转账通知",
  "mt.9": "创建选择银行转账的订单时发送",
  "mt.10": "付款提醒",
  "mt.11": "转账期限前约12小时发送",
  "mt.12": "取消批准",
  "mt.13": "批准取消时发送",
  "mt.14": "入住指南",
  "mt.15": "入住前一天 10:00 以后发送",
  "mt.16": "退房指南",
  "mt.17": "退房前一天 18:00 以后发送",
  "mt.18": "护照通知",
  "mt.19": "向自营订单客人自动发送上传网址",
  "mt.22": "客人姓名",
  "mt.23": "预订号",
  "mt.24": "入住日期",
  "mt.25": "退房日期",
  "mt.26": "入住时间",
  "mt.27": "退房时间",
  "mt.28": "人数",
  "mt.29": "总金额",
  "mt.30": "房源名称",
  "mt.31": "房源网址",
  "mt.32": "设施名称",
  "mt.33": "设施邮箱",
  "mt.34": "免费取消期限",
  "mt.35": "转账期限(小时)",
  "mt.36": "银行名称",
  "mt.37": "支行名称",
  "mt.38": "账户类型",
  "mt.39": "账号",
  "mt.40": "账户名",
  "mt.41": "退款说明(取消)",
  "mt.42": "护照网址",
  "mt.43": "加载失败 ({p0})",
  "mt.44": "保存失败 ({p0})",
  "mt.45": "发送时机已保存。将从下次定时处理（每小时0分）开始生效。",
  "mt.46": "自动发送关闭",
  "mt.47": "订单确定后{p0}分钟发送",
  "mt.48": "入住",
  "mt.49": "退房",
  "mt.50": "{p0}{p1} {p2}:00 以后发送",
  "mt.51": "转账期限前{p0}小时发送",
  "mt.52": "有未保存的更改。要放弃并切换吗？",
  "mt.53": "已保存。将从下次发送开始生效。",
  "mt.54": "测试发送失败 ({p0})",
  "mt.55": "已向 {p0} 发送测试邮件。",
  "mt.56": "邮件模板",
  "mt.57": "可编辑自动发送给客人的邮件主题和正文。",
  "mt.58": "加载中…",
  "mt.59": "未创建",
  "mt.60": "发送时机",
  "mt.61": "自动发送",
  "mt.62": "基准事件",
  "mt.63": "订单确定的",
  "mt.64": "立即",
  "mt.65": "{p0}小时后",
  "mt.66": "{p0}分钟后",
  "mt.67": "发送",
  "mt.68": "的",
  "mt.69": "以后发送",
  "mt.70": "※定时处理每小时0分运行，因此时间以“小时”为单位（“订单确定后N分钟”也会在下一次每小时运行时发送）。设置对所有语言・所有房源通用。",
  "mt.71": " 护照通知的自动发送只针对自营订单（OTA订单通过消息功能手动分享）。",
  "mt.72": "恢复",
  "mt.73": "保存中…",
  "mt.74": "保存时机",
  "mt.75": "转账期限的",
  "mt.76": "小时前",
  "mt.77": "（转账期限为预订后",
  "mt.78": "小时）",
  "mt.79": "※定时处理每小时0分运行，因此时间以“小时”为单位。设置对所有语言・所有房源通用。",
  "mt.80": " 如果订单在发送时间之后才确定，会在确定后的第一次定时处理中发送。",
  "mt.81": "主题",
  "mt.82": "正文",
  "mt.83": "写作技巧：",
  "mt.85": " 或 ",
  "mt.87": " 会成为标题，",
  "mt.89": " 连续2行以上会变成表格。",
  "mt.90": " 是分隔线。",
  "mt.91": "插入项目（点击插入）",
  "mt.92": "测试发送（使用编辑中的内容・示例订单发送／无需保存）",
  "mt.93": "发送中…",
  "mt.94": "测试发送",
  "mt.95": "最后更新：",
  "mt.96": "保存",
  "mt.97": "预览（示例订单）",
  "mt.98": "手机",
  "mt.99": "主题：",
};

const en: Record<AdminKey, string> = {
  'clnCk.result': 'Checklist ({n} item(s) checked)', 'clnCk.noResult': 'No checklist record (reported before the checklist was introduced, or completed manually by an admin)', 'clnCk.checkedBy': 'Reported by: {s}  {t}',
  'clnCk.tab': 'Checklist', 'clnCk.title': 'Cleaning checklist', 'clnCk.desc': 'Cleaners must check every item registered here before submitting a completion report. Add common items for all properties, plus property-specific items.',
  'clnCk.scope': 'Applies to', 'clnCk.common': 'All properties', 'clnCk.inherit': 'This property also shows the {n} common item(s).', 'clnCk.empty': 'No items',
  'clnCk.inactive': 'inactive', 'clnCk.save': 'Save', 'clnCk.cancel': 'Cancel', 'clnCk.edit': 'Edit', 'clnCk.disable': 'Disable', 'clnCk.enable': 'Enable',
  'clnCk.del': 'Delete', 'clnCk.confirmDel': 'Delete "{s}"? (Past report records are kept.)', 'clnCk.ph': 'e.g. Remove hair from bathroom drain', 'clnCk.add': 'Add',
  loading: 'Loading...', menu: 'Menu', logout: 'Log out', language: 'Language', legacyUser: 'Shared password (transition)',
  'nav.dashboard': 'Dashboard', 'nav.calendar': 'Calendar', 'nav.cleaning': 'Cleaning', 'nav.reservations': 'Reservations',
  'nav.messages': 'Guest messages', 'nav.properties': 'Properties', 'nav.passport': 'Passports', 'nav.checkin': 'Check-in ID check',
  'nav.settings': 'Facility settings', 'nav.adminUsers': 'Admin accounts', 'nav.otaReview': 'OTA sync review', 'rsv.otaIncomplete': 'Guest info needed', 'rsv.otaCancelSuspected': 'Possibly cancelled', 'rsv.csConfidence': 'Confidence', 'rsv.csStreak': 'missed {p0}x', 'rsv.cs_HIGH': 'High', 'rsv.cs_MEDIUM': 'Medium', 'rsv.cs_LOW': 'Low', 'rsv.cs_UID_MISSING': 'Event removed from the OTA calendar', 'rsv.cs_NO_COVERAGE': 'No event for these dates in the OTA calendar', 'rsv.cs_PARTIAL_COVERAGE': 'Only partly matches the OTA dates', 'rsv.cs_SOURCE_BLOCK_REMOVED': 'Source Airbnb block was removed', 'rsv.cs_FEED_EMPTIED': 'No upcoming events in the OTA calendar', 'rsv.cs_DATE_MISMATCH': 'Dates differ from the OTA calendar', 'rsv.otaActiveInFeed': 'Active on OTA', 'rsv.cs_ACTIVE_IN_FEED': 'Cancelled here, but still active on the OTA',
  'rsv.otaReview': 'Review (OTA)', 'rsv.otaDateChanged': 'Dates changed', 'rsv.otaMissing': 'Removed from the OTA calendar', 'rsv.otaOverlap': 'Overlaps another reservation', 'rsv.otaUnset': 'Not set', 'rsv.otaCode': 'OTA code', 'rsv.otaGoReview': 'Handle in OTA sync review',
  'login.title': 'Admin login', 'login.id': 'Login ID', 'login.pw': 'Password', 'login.submit': 'Log in', 'login.checking': 'Checking...',
  'login.noServer': 'Cannot connect to the API server', 'login.failed': 'Login failed',
  'db.attention': '⚠ {n} reservations need attention',
  'db.attentionText': 'These were paid after the hold expired, so the dates could not be secured. Re-secure the dates or issue a refund.',
  'db.viewList': 'View list →', 'db.total': 'All reservations', 'db.newWeek': 'New in the last 7 days', 'db.newWeekSub': 'Reservations received in the past 7 days',
  'db.pending': 'Awaiting approval', 'db.availTitle': 'Vacant days (today to end of next month)', 'db.availTip': '{name} ({v}/{t} days vacant)', 'db.days': 'days', 'db.daysUnit': ' days',
  'db.noProps': 'No properties', 'db.checkin': 'Check-in', 'db.checkout': 'Check-out', 'db.staying': 'Occupied', 'db.vacant': 'Vacant',
  'db.cleaning': 'Cleaning', 'db.cleaningErr': 'Could not load cleaning information', 'db.cleanToday': 'Cleanings today',
  'db.cleanTodaySub': 'Done {d} / Left {r}', 'db.cleanTomorrow': 'Cleanings tomorrow', 'db.openCleaning': 'Open cleaning →',
  'db.unassigned': 'Unassigned this week', 'db.unassignedSub': 'Next 7 days, no staff assigned',
  'db.upcoming': 'Check-ins and check-outs in the next 7 days', 'db.noUpcoming': 'Nothing scheduled in the next 7 days',
  'db.colProp': 'Property', 'db.colGuests': 'Guests', 'db.guestsN': '{n}', 'db.colPassport': 'Passport', 'db.colCheckinSt': 'Check-in status',
  'db.registered': 'Registered', 'db.notRegistered': 'Not registered', 'db.csNone': 'Not started', 'db.csProgress': 'In progress', 'db.csDone': 'Done',
  "cln.stPending": "🧹 Not started",
  "cln.stProgress": "🔄 In progress",
  "cln.stDone": "✅ Done",
  "cln.stReview": "⚠️ Needs review",
  "cln.stCancelled": "Cancelled",
  "cln.err": "An error occurred ({s})",
  "clnA.0": "Unassigned",
  "clnD.0": "Loading…",
  "clnD.1": "Today's status",
  "clnD.2": "↻ Refresh",
  "clnD.3": "Cleanings today",
  "clnD.4": "",
  "clnD.5": "Done",
  "clnD.6": "/ Left",
  "clnD.7": "Overdue",
  "clnD.8": "Before today, not started or in progress",
  "clnD.9": "Needs review",
  "clnD.10": "Needs your decision",
  "clnD.11": "Unassigned this week",
  "clnD.12": "Next 7 days",
  "clnD.13": "Cleanings tomorrow",
  "clnD.14": "⚠ Overdue",
  "clnD.15": "⚠️ Needs review",
  "clnD.16": "Cleaning tasks for the next 7 days ({p0}–{p1})",
  "clnD.17": "No cleanings in the next 7 days.",
  "clnD.18": "* Click a row to open that day in the calendar (change status or staff there).",
  "clnD.19": "Date",
  "clnD.20": "Property",
  "clnD.21": "Staff",
  "clnD.22": "Planned time",
  "clnD.23": "Status",
  "clnT.0": "This week",
  "clnT.1": "Next week",
  "clnT.2": "Last week",
  "clnT.3": "This month",
  "clnT.4": "Last month",
  "clnT.5": "Custom",
  "clnT.6": "Please enter a valid period.",
  "clnT.7": "Period",
  "clnT.8": "Start date",
  "clnT.10": "End date",
  "clnT.11": "Property",
  "clnT.12": "All properties",
  "clnT.13": "Staff",
  "clnT.14": "All staff",
  "clnT.15": "Unassigned only",
  "clnT.16": " (inactive)",
  "clnT.17": "Status",
  "clnT.18": "Done and needs review",
  "clnT.19": "Done only",
  "clnT.20": "Needs review only",
  "clnT.21": "All unfinished",
  "clnT.22": "Not started",
  "clnT.23": "In progress",
  "clnT.24": "Needs review",
  "clnT.25": ": ",
  "clnT.26": "",
  "clnT.27": "/ Supplies total",
  "clnT.28": "JPY",
  "clnT.29": "(Showing up to 500. Narrow the period.)",
  "clnT.30": "Loading…",
  "clnT.31": "No matching cleaning tasks.",
  "clnT.32": "Date",
  "clnT.33": "Start",
  "clnT.34": "End",
  "clnT.35": "Result",
  "clnT.36": "Supplies",
  "clnT.37": "Approval",
  "clnT.38": "¥{p0}",
  "clnT.39": "Review",
  "clnT.40": "Review",
  "clnT.41": "Details",
  "clnT.42": "Planned time",
  "clnT.43": "* Click a row to open that day in the calendar.",
  "clnT.44": "✔ Approved",
  "clnT.45": "✖ Sent back",
  "clnT.46": "Not reviewed",
  "clnT.47": "✔ Approved",
  "clnT.48": "✖ Rejected (sent back)",
  "clnT.49": "💬 Comment",
  "clnT.50": "Please enter a comment (what needs to be redone) to reject.",
  "clnT.51": "Please enter a comment.",
  "clnT.52": " ({p0} staff notified)",
  "clnT.53": " (no staff assigned, no notification sent)",
  "clnT.54": "Approved{p0}",
  "clnT.55": "Rejected and sent back{p0}",
  "clnT.56": "Comment sent{p0}",
  "clnT.57": "Review cleaning result",
  "clnT.58": "Work time",
  "clnT.59": "Reason for review",
  "clnT.60": "Note",
  "clnT.61": "Supplies purchased",
  "clnT.62": "Manager's note",
  "clnT.63": "History",
  "clnT.64": "Manager",
  "clnT.65": "Cleaner",
  "clnT.66": "·",
  "clnT.67": "Comment (sent to the cleaning staff. ",
  "clnT.68": "Required to reject",
  "clnT.69": ")",
  "clnT.70": "e.g. Water spots remain on the bathroom mirror. Please wipe it again.",
  "clnT.71": "Message to the staff",
  "clnT.72": "* Only completed cleanings can be approved or rejected. Comments can always be sent.",
  "clnT.73": "Reject and send back?",
  "clnT.74": "The status returns to \"In progress\" and the staff will redo the work and report again. The end time will be cleared.",
  "clnT.75": "Cancel",
  "clnT.76": "Processing…",
  "clnT.77": "Reject and send back",
  "clnT.78": "Open in calendar",
  "clnT.79": "💬 Send comment only",
  "clnT.80": "✖ Reject",
  "clnT.81": "✔ Approved",
  "clnT.82": "Close",
  "clnT.83": "Photos ({p0})",
  "clnT.84": "Before",
  "clnT.85": "After",
  "clnT.86": "Lost item",
  "clnT.87": "Damage / stain",
  "clnT.88": "Other",
  "clnT.89": "{p0} photo(s)",
  "clnM.0": "An error occurred ({p0})",
  "clnM.1": "Password must be at least 8 characters",
  "clnM.2": "Passwords do not match",
  "clnM.3": "Login set up for \"{p0}\". Login URL: {p1}/cleaner",
  "clnM.4": "Saved \"{p0}\"",
  "clnM.5": " (assigned to {p0} unassigned cleaning tasks)",
  "clnM.6": "Deactivate \"{p0}\"?\nThey will be removed from unstarted cleaning tasks from today on (past work records are kept).",
  "clnM.7": "Activated \"{p0}\"",
  "clnM.8": " (assigned to {p0} cleaning tasks)",
  "clnM.9": "Deactivated \"{p0}\"",
  "clnM.10": " (removed from {p0} cleaning tasks)",
  "clnM.11": "Filter by property",
  "clnM.12": "All properties",
  "clnM.13": "+ Add staff",
  "clnM.14": "Loading…",
  "clnM.15": "No staff registered yet.",
  "clnM.16": "No staff for this property.",
  "clnM.17": "Inactive",
  "clnM.18": "Upcoming",
  "clnM.19": " / Done",
  "clnM.20": "",
  "clnM.21": "📍 Preferred area:",
  "clnM.22": "🔑 Login ID:",
  "clnM.23": "🔑 No login set",
  "clnM.24": "Properties:",
  "clnM.25": "None",
  "clnM.26": "Edit",
  "clnM.27": "Reset login",
  "clnM.28": "Set up login",
  "clnM.29": "Deactivate",
  "clnM.30": "Activate",
  "clnM.31": "* Staff are deactivated, not deleted. When you assign a property, they are automatically added to its unassigned cleaning tasks from today on.",
  "clnM.32": "Cleaner login (",
  "clnM.33": ")",
  "clnM.34": "Login ID (letters, numbers, . _ @ -, 3–64 characters)",
  "clnM.35": "New password (at least 8 characters)",
  "clnM.36": "New password (confirm)",
  "clnM.37": "* Login URL:",
  "clnM.38": ". Give the ID and password to the staff member. Resetting will log them out once.",
  "clnM.39": "Cancel",
  "clnM.40": "Saving…",
  "clnM.41": "Save",
  "clnM.42": "Add staff",
  "clnM.43": "Edit staff",
  "clnM.44": "Name *",
  "clnM.45": "Phone",
  "clnM.46": "Email",
  "clnM.47": "Preferred area",
  "clnM.48": "e.g. Shinjuku, Nakano",
  "clnM.49": "Address (optional)",
  "clnM.50": "Assigned properties",
  "clnP.0": "Direct",
  "clnP.1": "Manual",
  "clnP.2": "🧹 Not started",
  "clnP.3": "🔄 In progress",
  "clnP.4": "✅ Done",
  "clnP.5": "⚠️ Needs review",
  "clnP.6": "Cancelled",
  "clnP.7": "Failed to load the cleaning schedule.",
  "clnP.8": "Failed to change staff.",
  "clnP.9": "Operation failed.",
  "clnP.10": "Enter the reason for \"Needs review\" (e.g. work stopped partway, broken supplies)",
  "clnP.11": "Please enter a reason.",
  "clnP.12": "End time must be after start time",
  "clnP.13": "Failed to save.",
  "clnP.14": "Failed to add the cleaning date.",
  "clnP.15": "Cleaning",
  "clnP.16": "+ Add cleaning",
  "clnP.17": "🏠 Dashboard",
  "clnP.18": "📅 Calendar",
  "clnP.19": "📋 Tasks",
  "clnP.20": "✅ Completed",
  "clnP.21": "👤 Staff",
  "clnP.22": "🛎️ Check-ins",
  "clnP.23": "Previous month",
  "clnP.24": "‹ Prev",
  "clnP.25": "/",
  "clnP.26": "",
  "clnP.27": "Next month",
  "clnP.28": "Next ›",
  "clnP.29": "Loading…",
  "clnP.30": "{p0} ({p1})\nCleaning date (check-out): {p2}",
  "clnP.31": "{p0} ({p1})\nCheck-in: {p2}",
  "clnP.32": " cleaning",
  "clnP.33": "No cleaning on this day.",
  "clnP.34": " (",
  "clnP.35": ")",
  "clnP.36": "Cleaning date (check-out):",
  "clnP.37": "Planned:",
  "clnP.39": "Not set",
  "clnP.40": "/ Check-in:",
  "clnP.41": "Actual:",
  "clnP.42": "⚠️ Reason for review:",
  "clnP.43": "Note:",
  "clnP.44": "Staff:",
  "clnP.45": " (inactive)",
  "clnP.46": "Unassigned",
  "clnP.47": "Start",
  "clnP.48": "Done",
  "clnP.49": "Needs review",
  "clnP.50": "Back to not started",
  "clnP.51": "Staff",
  "clnP.52": "Time & note",
  "clnP.53": "Cancel this cleaning?",
  "clnP.54": "View reservation",
  "clnP.55": "🛎️ Check-ins this day (",
  "clnP.56": ")",
  "clnP.57": "Check-out (cleaning date):",
  "clnP.58": "Change staff",
  "clnP.59": "/",
  "clnP.60": "No staff registered. Add them in the \"Staff\" tab.",
  "clnP.61": "Assigned here",
  "clnP.62": "Inactive",
  "clnP.63": "* With multiple staff, it is complete when any one of them reports completion.",
  "clnP.64": "Cancel",
  "clnP.65": "Save",
  "clnP.66": "Planned start",
  "clnP.67": "Planned end",
  "clnP.68": "Use property times (",
  "clnP.69": "Clear times",
  "clnP.70": "Note (also shown to cleaning staff)",
  "clnP.71": "Saving…",
  "clnP.72": "Add cleaning",
  "clnP.73": "Add one cleaning manually, separate from reservations (not possible if the day already has one).",
  "clnP.74": "Property",
  "clnP.75": "Select",
  "clnP.76": "Cleaning date",
  "clnP.77": "Planned start (optional)",
  "clnP.78": "Planned end (optional)",
  "clnP.79": "Note (optional)",
  "clnP.80": "Add",
  "rsv.0": "Approved",
  "rsv.1": "Confirmed",
  "rsv.2": "Cancel requested",
  "rsv.3": "Cancellation approved",
  "rsv.4": "Passport received",
  "rsv.5": "Check-in today",
  "rsv.6": "Staying",
  "rsv.7": "Check-out today",
  "rsv.8": "Finished",
  "rsv.9": "Failed to register the OTA reservation.",
  "rsv.10": "OTA reservation registered. Share the \"fixed URL, reservation number, and check-in date\" below with the guest via the OTA messaging feature.",
  "rsv.11": "Admin authentication failed (401). Check the ADMIN_PASSWORD_HASH setting.",
  "rsv.12": "Failed to load reservations ({p0}){p1}",
  "rsv.13": "Cannot connect to the API server. Check that the API is running.",
  "rsv.14": "Failed to load reservations",
  "rsv.15": "Operation failed ({p0})",
  "rsv.16": "Cancelled and fully refunded via Stripe.",
  "rsv.17": "Cancelled. {p0}",
  "rsv.18": "\nCancellation email sent to the guest.",
  "rsv.19": "\n* Failed to email the guest. Please contact them from the Guest messages page.",
  "rsv.20": "Update failed ({p0})",
  "rsv.21": "Awaiting payment",
  "rsv.22": "Awaiting transfer",
  "rsv.23": "Confirmed",
  "rsv.24": "Cancelled",
  "rsv.25": "Expired",
  "rsv.26": "Refunded",
  "rsv.27": "⚠ Action needed (paid after expiry)",
  "rsv.28": "⇩ Import from Booking",
  "rsv.29": "⇩ Import from Airbnb",
  "rsv.30": "Close",
  "rsv.31": "+ Add OTA reservation",
  "rsv.32": "Reload",
  "rsv.33": "All",
  "rsv.34": "Filter by property",
  "rsv.35": "All properties",
  "rsv.36": "Sort",
  "rsv.37": "Newest first",
  "rsv.38": "Check-in (ascending)",
  "rsv.39": "Check-in (descending)",
  "rsv.40": "Display filters",
  "rsv.41": "Show:",
  "rsv.42": "Also show reservations whose check-out date has passed",
  "rsv.43": "Include past",
  "rsv.44": "Also show cancelled and refunded reservations",
  "rsv.45": "Include cancelled",
  "rsv.46": "Filter by status (multiple)",
  "rsv.47": "Status:",
  "rsv.48": "Clear",
  "rsv.49": "Property",
  "rsv.50": "Click to toggle ascending/descending",
  "rsv.51": "Check-in",
  "rsv.52": "Check-out",
  "rsv.53": "Guests",
  "rsv.54": "Free cancellation until",
  "rsv.55": "Status",
  "rsv.56": "Actions",
  "rsv.57": "{p0}",
  "rsv.58": "Details",
  "rsv.59": "No reservations",
  "rsv.60": "Reservation details",
  "rsv.61": "Reservation no.",
  "rsv.62": "Guest name",
  "rsv.63": "Email",
  "rsv.64": "Amount",
  "rsv.65": "Payment status:",
  "rsv.66": "Manual setting",
  "rsv.67": "Cancellation requested",
  "rsv.68": "Requested at:",
  "rsv.69": " (UTC)",
  "rsv.70": "Reason:",
  "rsv.71": "free cancellation until (until",
  "rsv.72": " days before check-in)",
  "rsv.73": "No refund after that",
  "rsv.74": "Confirm payment and mark this reservation as confirmed? (Stripe payment will be checked)",
  "rsv.75": "Confirm payment",
  "rsv.76": "Re-secure the dates and confirm the reservation? (Not possible if unavailable)",
  "rsv.77": "Re-secure dates and confirm",
  "rsv.78": "Issue a full refund via Stripe? (Cannot be undone)",
  "rsv.79": "Full refund",
  "rsv.80": "Confirm the transfer and mark this reservation as confirmed?",
  "rsv.81": "Confirm transfer",
  "rsv.82": "This is within the free cancellation period, so it will be cancelled and fully refunded via Stripe (cannot be undone).\nContinue?",
  "rsv.83": "Cancel this reservation? (Inventory is released, no refund)",
  "rsv.84": "Approve cancellation",
  "rsv.85": "Cancel reservation",
  "rsv.86": "Contact guest",
  "ota.0": "Other OTA",
  "ota.1": "Property",
  "ota.2": "Select",
  "ota.3": "Check-in",
  "ota.4": "Check-out",
  "ota.5": "Guests",
  "ota.6": "Guest name (optional, OTA display name)",
  "ota.7": "OTA reservation no. (optional)",
  "ota.8": "Register and issue URL",
  "iss.0": "Fixed URL",
  "iss.1": "Upload URL",
  "iss.2": "Copied",
  "iss.3": "Copy",
  "iss.4": "URL with reservation no.",
  "iss.5": "Reservation no.",
  "iss.6": "Check-in date:",
  "iss.7": "* Give this URL directly to the guest (email, OTA message, etc.). Old URLs are disabled; this is the only valid one.",
  "iss.8": "Close",
  "iss.9": "To:",
  "iss.10": "Subject",
  "iss.11": "Body (template, editable)",
  "iss.12": "Please review before sending",
  "iss.13": "Sending…",
  "iss.14": "Send email",
  "iss.15": "* No email is needed for OTA reservations. Copy the above and contact the guest via the OTA site messages.",
  "abi.0": "Confirmation code",
  "abi.8": "No data rows found.",
  "abi.9": "No reservation rows to import.",
  "abi.10": "Loaded {p0} reservation rows (property codes: {p1}). Review and press \"Import\".",
  "abi.11": "Failed to read the file: {p0}",
  "abi.12": "Please select a file.",
  "abi.13": "Import failed.",
  "abi.14": "Import reservations from Airbnb",
  "abi.15": "Select the Airbnb reservations CSV. The file is parsed in your browser.",
  "abi.16": "The property is detected from the CSV \"Listing\" column (property codes such as SD / FT / GW), so you do not need to select it.",
  "abi.17": "Amounts are not imported (reservation data only). Confirmation codes already registered are not duplicated.",
  "abi.18": "File (Airbnb reservations CSV)",
  "abi.19": "Added",
  "abi.20": "Skipped",
  "abi.21": "Error",
  "abi.22": "Total",
  "abi.23": "Row",
  "abi.24": "Property",
  "abi.25": "Result",
  "abi.26": "Reason",
  "abi.27": "Close",
  "abi.28": "Cancel",
  "abi.29": "Importing…",
  "abi.30": "Import{p0}",
  "bki.0": "Reservation no.",
  "bki.9": "No data rows found.",
  "bki.10": "Required columns not found: {p0}. Check that this is a Booking.com reservations file.",
  "bki.11": "No reservation rows to import.",
  "bki.12": "Loaded {p0} reservation rows. Review and press \"Import\".",
  "bki.13": "Failed to read the file: {p0}",
  "bki.14": "Please select a property.",
  "bki.15": "Please select a file.",
  "bki.16": "Import failed.",
  "bki.17": "Import reservations from Booking.com",
  "bki.18": "Select a property and the Booking.com reservations file (.xls / .xlsx / .csv).",
  "bki.19": "The file is parsed in your browser and each reservation is registered for this property (as confirmed, with an auto-generated reservation number).",
  "bki.20": "Reservation numbers already registered are not duplicated.",
  "bki.21": "Property",
  "bki.22": "Select",
  "bki.23": "File",
  "bki.24": "Added",
  "bki.25": "Skipped",
  "bki.26": "Error",
  "bki.27": "Total",
  "bki.28": "Row",
  "bki.29": "Result",
  "bki.30": "Reason",
  "bki.31": "Close",
  "bki.32": "Cancel",
  "bki.33": "Importing…",
  "bki.34": "Import{p0}",
  "abi.count": " ({n})",
  "bki.count": " ({n})",
  "cal.0": "Manual block",
  "cal.1": "{p0}/{p1}",
  "cal.2": "Sync failed",
  "cal.3": "Sync complete: imported {p0} nights",
  "cal.4": "Error: {p0}",
  "cal.5": "Previous month",
  "cal.6": "Next month",
  "cal.7": "This month",
  "cal.8": "Select the property to sync in the tabs",
  "cal.9": "Sync this property's OTA calendar now",
  "cal.10": "Syncing…",
  "cal.11": "Sync this property now",
  "cal.12": "All properties",
  "cal.13": "Confirmed",
  "cal.14": "Awaiting transfer",
  "cal.15": "Stop (Airbnb/OTA import)",
  "cal.16": "| OTA Stop:",
  "cal.18": "",
  "cal.19": "{p0} / Stopped by {p1} (not bookable)",
  "cal.20": "■ Stop (",
  "cal.21": ")",
  "pp.0": "Male",
  "pp.1": "Female",
  "pp.2": "Other",
  "pp.3": "Direct",
  "pp.4": "Other OTA",
  "pp.5": "Could not load the file.",
  "pp.6": "Failed to get the registry CSV.",
  "pp.7": "Delete this file? This cannot be undone.",
  "pp.8": "Delete all of this guest's registration data and images and reset to unregistered?\nThis cannot be undone.",
  "pp.9": "Guest's registration deleted.",
  "pp.10": "Failed to delete guest data.",
  "pp.11": "Failed to issue the URL.",
  "pp.12": "This is an OTA reservation. Copy the \"fixed URL, reservation number, and check-in date\" below and contact the guest via the OTA site messages.",
  "pp.13": "URL issued. Review the email and press \"Send email\" if it looks right.",
  "pp.14": "Issuing an immediate URL instantly disables existing URLs for this reservation,\n",
  "pp.15": "and only the new URL will be valid.\n",
  "pp.16": "If you already gave the guest an old URL, it will stop working.\n\n",
  "pp.17": "Continue?",
  "pp.18": "Failed to issue the rescue URL.",
  "pp.19": "Showing the valid fixed URL and reservation number. Give them to the guest via the OTA site messages.",
  "pp.20": "Issued one valid upload URL. Give it directly to the guest (old URLs were disabled).",
  "pp.21": "Failed to send the email.",
  "pp.22": "(Email skipped: sending is not configured)",
  "pp.23": "Upload URL sent by email.",
  "pp.24": "Failed to register the OTA reservation.",
  "pp.25": "OTA reservation registered. Share the \"fixed URL, reservation number, and check-in date\" below with the guest via the OTA messaging feature.",
  "pp.26": "Passports",
  "pp.27": "Close",
  "pp.28": "+ Add OTA reservation",
  "pp.29": "Issued URL",
  "pp.30": "Property",
  "pp.31": "All properties",
  "pp.32": "Include past check-in dates",
  "pp.33": "",
  "pp.34": "Loading…",
  "pp.35": "Property / Reservation no.",
  "pp.36": "Type",
  "pp.37": "Click to toggle ascending/descending",
  "pp.38": "Check-in",
  "pp.39": "Registration",
  "pp.40": "Expiry",
  "pp.41": "No matching reservations",
  "pp.42": "Done",
  "pp.43": "Incomplete",
  "pp.44": "Cancelled",
  "pp.45": "Expired",
  "pp.46": "Valid",
  "pp.47": "Actions",
  "pp.48": "Details",
  "pp.49": "Issue URL",
  "pp.50": "Recreate one valid URL and give it directly to the guest",
  "pp.51": "Issue now",
  "pp.52": "Download guest registry CSV",
  "pp.53": "Registry CSV",
  "pp.54": "(",
  "pp.55": ")",
  "pp.56": "Download registry CSV",
  "pp.58": "Guest ",
  "pp.59": ": {p0}",
  "pp.60": " (not registered)",
  "pp.61": "Registered",
  "pp.62": "Delete this guest's data and images and reset to unregistered",
  "pp.63": "Reset registration",
  "pp.64": "Date of birth:",
  "pp.65": "/ Nationality:",
  "pp.66": "Address:",
  "pp.67": "/ Next destination:",
  "pp.68": "No files",
  "pp.69": "Passport image #{p0}",
  "pp.70": "Click to enlarge",
  "pp.71": "Click to view",
  "pp.72": "Delete",
  "pp.73": "Passport image (enlarged)",
  "ci.0": "Not submitted",
  "ci.1": "Submitted (awaiting review)",
  "ci.2": "Reviewing",
  "ci.3": "Resubmission requested",
  "ci.4": "Mismatch",
  "ci.5": "Verified",
  "ci.6": "Needs attention",
  "ci.7": "Not started",
  "ci.8": "Done",
  "ci.9": "Male",
  "ci.10": "Female",
  "ci.11": "Other",
  "ci.12": "The property for this reservation has no facility code (reservation number prefix). Set one in Properties (e.g. SD).",
  "ci.13": "Check-in URL issued. Normally give the guest the dated URL; if only the OTA fixed URL field is available, give the facility fixed URL.",
  "ci.14": "Failed to send the email.",
  "ci.15": "(Email skipped: sending is not configured)",
  "ci.16": "Upload URL sent by email.",
  "ci.17": "Failed to register the OTA reservation.",
  "ci.18": "OTA reservation registered. Share the \"fixed URL, reservation number, and check-in date\" below with the guest via the OTA messaging feature.",
  "ci.19": "Could not load the reservation.",
  "ci.20": "Operation failed: {p0}",
  "ci.21": "Request resubmission",
  "ci.22": "Record as mismatch",
  "ci.23": "Record as needs attention",
  "ci.24": "Click to enlarge",
  "ci.25": "Loading…",
  "ci.26": "Check-in ID verification",
  "ci.27": "Close",
  "ci.28": "+ Add OTA reservation",
  "ci.29": "This is a legally required ID check. Compare the guest's ",
  "ci.30": "face photo",
  "ci.31": " with their ",
  "ci.32": "passport",
  "ci.33": " and record the result.",
  "ci.34": "Check-in URL",
  "ci.35": "Check-in URL (with date)",
  "ci.36": "Copied",
  "ci.37": "Copy",
  "ci.38": "Facility fixed URL (no date)",
  "ci.39": "Facility code:",
  "ci.40": "/ Check-in date:",
  "ci.41": "* With the dated URL, the guest goes straight to ID verification (face photo, passport) without entering anything.",
  "ci.42": "* The facility fixed URL has no date, so it can be pasted into the OTA fixed URL field as is (the guest just selects the check-in date).",
  "ci.43": "Issued URL",
  "ci.44": "Property",
  "ci.45": "All properties",
  "ci.46": "Include past check-in dates",
  "ci.47": "",
  "ci.48": "Click to toggle ascending/descending",
  "ci.49": "Check-in date",
  "ci.50": "Facility",
  "ci.51": "ID check",
  "ci.52": "Verified",
  "ci.53": "No matching reservations",
  "ci.54": "Actions",
  "ci.55": "Review",
  "ci.56": "Issue the check-in URL for the guest (facility code and date)",
  "ci.57": "Issue URL",
  "ci.58": "(",
  "ci.59": ")",
  "ci.61": "ID check:",
  "ci.62": "Guest ",
  "ci.63": ": {p0}",
  "ci.64": " (name not registered)",
  "ci.65": "Submitted from:",
  "ci.66": "Facility tablet",
  "ci.67": "Guest's device",
  "ci.68": "Date of birth:",
  "ci.69": "Nationality:",
  "ci.70": "Note:",
  "ci.71": "Mark verified",
  "ci.72": "Enlarged image",
  "ci.73": "Send back",
  "ci.74": "Reason",
  "ci.75": " (optional)",
  "ci.76": "Face and passport do not match, etc.",
  "ci.77": "Why ID verification cannot continue, etc.",
  "ci.78": "Image is unclear, etc.",
  "ci.79": "Cancel",
  "ci.80": "Record",
  "adm.gender": "/ Gender:",
  "adm.passportNo": "/ Passport no.:",
  "adm.prevAcc": "/ Previous stay:",
  "msg.0": "Awaiting payment",
  "msg.1": "Awaiting transfer",
  "msg.2": "Confirmed",
  "msg.3": "Cancelled",
  "msg.4": "Expired",
  "msg.5": "Refunded",
  "msg.6": "Needs attention",
  "msg.7": "Sent",
  "msg.8": "Not sent (no email settings)",
  "msg.9": "Failed",
  "msg.10": "Received",
  "msg.11": "Error ({p0})",
  "msg.12": "Delete the message history with {p0} ({p1} messages)?\n* Reservation data is not deleted. This cannot be undone.",
  "msg.13": "Delete failed",
  "msg.14": "Cannot connect to the API server",
  "msg.15": "Failed to load",
  "msg.16": "Guest messages",
  "msg.17": "Reload",
  "msg.18": "Search by name or email",
  "msg.19": "Filter by property",
  "msg.20": "All properties",
  "msg.21": "Sort",
  "msg.22": "Most recent",
  "msg.23": "Check-in (ascending)",
  "msg.24": "Check-in (descending)",
  "msg.25": "",
  "msg.26": "Loading...",
  "msg.27": "No matching guests",
  "msg.28": "Guest / Last message",
  "msg.29": "Check-in",
  "msg.30": "Status",
  "msg.31": "Last activity",
  "msg.32": "No reservation",
  "msg.33": "(no name)",
  "msg.34": "Auto",
  "msg.35": "Send",
  "msg.36": "No messages yet",
  "msg.37": " · {p0}",
  "msg.38": " · {p0} reservations",
  "msg.40": "Passport sent",
  "msg.41": "Non-cancellable",
  "msg.42": "Menu",
  "msg.43": "No messages to delete",
  "msg.44": "Delete",
  "msg.45": "\"{p0}\"",
  "msg.46": "this message",
  "msg.47": "Delete {p0}?\n* This cannot be undone.",
  "msg.48": "Message deleted",
  "msg.49": "Please enter a subject",
  "msg.50": "Please enter the message",
  "msg.51": "Send the email to {p0}?",
  "msg.52": "Received message recorded",
  "msg.53": "Recorded, but not actually sent because email is not configured (RESEND_API_KEY)",
  "msg.54": "Sent",
  "msg.55": "Failed to send",
  "msg.56": "Failed to update passport status",
  "msg.57": "← Back to list",
  "msg.58": "Open in Reservations",
  "msg.60": "Check this when the passport is received",
  "msg.61": "Passport received",
  "msg.62": "No messages yet. You can send the first message from the form below.",
  "msg.63": "Guest",
  "msg.64": "Auto email",
  "msg.65": "Facility",
  "msg.66": "Error:",
  "msg.67": "⚠ Sender authentication failed (possible spoofing). Check the content carefully.",
  "msg.68": "From:",
  "msg.69": "Send email",
  "msg.70": "Record guest's reply",
  "msg.71": "Subject",
  "msg.72": "Subject (optional)",
  "msg.73": "Related reservation",
  "msg.74": "Type a message (Ctrl+Enter to send)",
  "msg.75": "Paste the guest's email body",
  "msg.76": "Processing...",
  "msg.77": "Record",
  "prop.0": "Active",
  "prop.1": "Inactive",
  "prop.2": "Max guests:",
  "prop.3": "",
  "prop.4": "Base guests:",
  "prop.5": "Extra fee:",
  "prop.6": "Cleaning fee:",
  "prop.7": "Min nights:",
  "prop.8": " nights",
  "prop.9": "Check-in:",
  "prop.10": "Check-out:",
  "prop.11": "Smoking:",
  "prop.12": "Allowed",
  "prop.13": "Not allowed",
  "prop.14": "Parties:",
  "prop.15": "Allowed",
  "prop.16": "Not allowed",
  "prop.17": "Free cancellation:",
  "prop.18": "until",
  "prop.19": " days before check-in",
  "prop.20": "iCal export URL",
  "prop.21": "Copy URL",
  "prop.22": "Copied",
  "prop.23": "Copy",
  "prop.24": "Not set — appears after you set a secret in \"Edit\"",
  "prop.25": "Property name",
  "prop.26": "Max guests",
  "prop.27": "Base guests",
  "prop.28": "Extra fee (JPY/person)",
  "prop.29": "Cleaning fee (JPY)",
  "prop.30": "Min nights",
  "prop.31": "Check-out",
  "prop.32": "Airbnb / OTA calendar import URL (iCal import)",
  "prop.33": "The ics URL exported from Airbnb etc. Imported every 15 minutes to block inventory.",
  "prop.icalBooking": "Booking.com iCal URL",
  "prop.icalBookingHint": "The ics URL exported from Booking.com. Reservations are registered automatically (guest info to be filled in), and Airbnb blocks on the same dates are auto-confirmed as duplicate blocks.",
  "prop.34": "iCal export secret",
  "prop.35": "Random string (e.g. 8f3a1c...)",
  "prop.36": "Export URL for this property (paste into \"Import another calendar\" on Airbnb):",
  "prop.37": "After you set and save a secret, the export URL for Airbnb is shown.",
  "prop.38": "Key rules & cancellation policy",
  "prop.39": "Smoking",
  "prop.40": "Parties",
  "prop.41": "Free cancellation deadline (days before check-in)",
  "prop.42": "e.g. 31 means free cancellation until 31 days before check-in; no refund after that (within 30 days).",
  "prop.43": "The specific deadline date (check-in date minus this number of days) is shown to guests when they book.",
  "prop.44": "Property details",
  "prop.45": "Area (㎡)",
  "prop.46": "Bedrooms",
  "prop.47": "Living rooms",
  "prop.48": "Floors",
  "prop.49": "Double beds",
  "prop.50": "Single beds",
  "prop.51": "Futons",
  "prop.52": "Double sofa beds",
  "prop.53": "Single sofa beds",
  "prop.54": "Toilets",
  "prop.55": "Baths / showers",
  "prop.56": "Highlights (one per line, shown on the home page card)",
  "prop.57": "Up to 10 guests\n5 bedrooms\nRooftop balcony\n3 min walk from station",
  "prop.58": "Each item is shown on the card with ✔. Leave empty to hide.",
  "prop.59": "\"View property\" link URL",
  "prop.60": "Link for the button on the home page property card. If empty, the regular property page (/property/ID) opens.",
  "prop.61": "OTA reservation number prefix",
  "prop.62": "e.g. SD",
  "prop.63": "Property prefix used for OTA (Airbnb/Booking.com etc.) reservation numbers (e.g. Stay Dream → SD). 2–4 letters/numbers. Properties without one cannot register OTA reservations. Numbers are issued as \"prefix-check-in date-6 random digits\" (e.g. SD-20261013-000742).",
  "prop.64": "Calendar abbreviation",
  "prop.65": "e.g. Stay",
  "prop.66": "Short name used to show the property on the /admin/calendar and /admin/cleaning calendars (optional, Japanese allowed). If not set, the property code (prefix SD / FT etc.) is shown.",
  "set.0": "Basic settings",
  "set.1": "Email templates",
  "set.2": "Check-in kiosk",
  "set.3": "Facility name",
  "set.4": "e.g. Stay Dream Hotel",
  "set.5": "Used as the email sender name and in the body.",
  "set.6": "Address",
  "set.7": "e.g. 1-2-3 ○○, Shibuya-ku, Tokyo",
  "set.8": "Phone",
  "set.9": "e.g. 03-1234-5678",
  "set.10": "Email",
  "set.11": "e.g. info@staydreamhotel.com",
  "set.12": "Used as the email Reply-To and contact address.",
  "set.13": "Bank",
  "set.14": "e.g. MUFG Bank",
  "set.15": "Branch",
  "set.16": "e.g. Shibuya branch",
  "set.17": "Account type",
  "set.18": "e.g. Ordinary",
  "set.19": "Account number",
  "set.20": "e.g. 1234567",
  "set.21": "Account holder",
  "set.22": "e.g. Stay Dream Co.",
  "set.23": "Transfer deadline (hours)",
  "set.24": "e.g. 72",
  "set.25": "Hours from booking until the transfer deadline. Inserted into the transfer instructions email.",
  "set.26": "Failed to load settings.",
  "set.27": "Failed to save.",
  "set.28": "Facility settings saved.",
  "set.29": "Facility settings",
  "set.30": "Set the facility information, bank account, etc. inserted into booking confirmation and transfer emails. Email timing is set in the \"Email templates\" tab.",
  "set.31": "Loading…",
  "set.32": "Facility information",
  "set.33": "Bank account",
  "set.34": "Other",
  "set.35": "Revert",
  "set.36": "Saving…",
  "set.37": "Save",
  "set.38": "Admin panel title",
  "set.39": "e.g. Stay Dream Hotel Admin",
  "set.40": "Name shown at the top left of the admin panel. If empty, the default site name is shown.",
  "set.41": "Booking site title",
  "set.42": "e.g. Stay Dream Hotel",
  "set.43": "Name shown at the top left of the guest booking site. If empty, the default site name is shown.",
  "cp.tab": "Repeater coupon",
  "rsv.coupon": "Coupon",
  "cp.desc": "A coupon shared across all properties for returning guests. Only one code is valid at a time; saving a new code immediately disables the previous one. The discount applies to the room charge only (not extra-guest fees, cleaning fees or tax).",
  "cp.status": "Status",
  "cp.st.NONE": "Not set",
  "cp.st.ACTIVE": "Active",
  "cp.st.PAUSED": "Paused",
  "cp.st.NOT_STARTED": "Not started",
  "cp.st.EXPIRED": "Expired",
  "cp.code": "Coupon code",
  "cp.codeHint": "Letters, digits and hyphens, 3–30 chars. Case-insensitive (stored in upper case).",
  "cp.rate": "Discount rate (%)",
  "cp.rateHint": "Integer from 1 to {n}",
  "cp.from": "Valid from",
  "cp.until": "Valid until",
  "cp.periodHint": "Judged by booking date (JST). Valid until 23:59 on the end date.",
  "cp.note": "Note (internal)",
  "cp.updated": "Last updated: {t} ({by})",
  "cp.usage": "Bookings with this code: {n}",
  "cp.save": "Save",
  "cp.saving": "Saving…",
  "cp.reset": "Reset",
  "cp.pause": "Pause",
  "cp.resume": "Resume",
  "cp.confirmReplace": "Changing the coupon code will immediately disable the current code ({s}). Continue?",
  "cp.confirmPause": "Pause coupon ({s})? It can no longer be used for new bookings (confirmed bookings are unchanged).",
  "cp.saved": "Saved",
  "cp.loadErr": "Failed to load",
  "cp.saveErr": "Failed to save",
  "cp.history": "Previous codes",
  "cp.noHistory": "None",
  "cp.guestText": "Guest message (for copy)",
  "cp.copy": "Copy",
  "cp.copied": "Copied",
  "dev.0": "Check-in kiosk (iPad) setup",
  "dev.1": "Initial setup for the iPad placed on site. Select a facility and press \"Set up this iPad\" ",
  "dev.2": "to generate the check-in URL. Add that URL to this iPad's home screen ",
  "dev.3": "as an \"App icon\". After that, guests do not need to enter a facility code ",
  "dev.4": "and can continue just by entering their check-in date.",
  "dev.5": "Loading…",
  "dev.6": "Facility code:",
  "dev.7": "There are no active facilities with a facility code (prefix).",
  "dev.8": "Set a prefix (e.g. SD) for each property in Properties.",
  "dev.9": "These facilities cannot be selected because they have no facility code (set it in Properties):",
  "dev.10": ", ",
  "dev.11": " — set up this iPad",
  "dev.12": "Add the URL below to this iPad's home screen.",
  "dev.13": "Open this URL",
  "dev.14": "Copied ✓",
  "dev.15": "Copy URL",
  "dev.16": "Adding to the home screen (iPad / Safari)",
  "dev.17": "Open it in Safari with \"Open this URL\".",
  "dev.18": "Tap the Share button (□↑) → \"Add to Home Screen\".",
  "dev.19": "Name it and tap \"Add\". From then on, launch it from that icon.",
  "au.0": "An error occurred ({p0})",
  "au.1": "Deactivate \"{p0}\"? They will be logged out immediately and can no longer log in.",
  "au.2": "Activated \"{p0}\"",
  "au.3": "Deactivated \"{p0}\"",
  "au.4": "Admin accounts",
  "au.5": "+ Add admin",
  "au.6": "Loading…",
  "au.7": "Display name",
  "au.8": "Login ID",
  "au.9": "Status",
  "au.10": "Last login",
  "au.11": "Actions",
  "au.12": "You",
  "au.13": "Active",
  "au.14": "Inactive",
  "au.15": "Rename",
  "au.16": "Change password",
  "au.17": "Deactivate",
  "au.18": "Activate",
  "au.19": "No admin accounts",
  "au.20": "* Accounts are deactivated, not deleted. Deactivated users are logged out immediately.",
  "au.21": "Password changed. Please log in again with the new password.",
  "au.22": "Close",
  "au.23": "Cancel",
  "au.24": "Saving…",
  "au.25": "Password must be at least {p0} characters",
  "au.26": "Passwords do not match",
  "au.27": "Added \"{p0}\"",
  "au.28": "Add admin",
  "au.29": "Add",
  "au.30": "Login ID (letters, numbers, . _ @ -, 3–64 characters)",
  "au.31": "Password (at least ",
  "au.32": " characters)",
  "au.33": "Password (confirm)",
  "au.34": "Display name changed",
  "au.35": "Change display name ({p0})",
  "au.36": "Save",
  "au.37": "Password changed for \"{p0}\"",
  "au.38": "Change password ({p0})",
  "au.39": "Change",
  "au.40": "Current password",
  "au.41": "New password (at least ",
  "au.42": "New password (confirm)",
  "au.43": "Changing it logs out every session of this account, including yours.",
  "au.44": "Changing it logs this person out immediately.",
  "mt.0": "Same day",
  "mt.1": "The day before",
  "mt.2": "{p0} days before",
  "mt.3": "After confirmation",
  "mt.4": "Based on check-in",
  "mt.5": "Based on check-out",
  "mt.6": "Booking confirmed",
  "mt.7": "Sent after payment or transfer is confirmed",
  "mt.8": "Transfer instructions",
  "mt.9": "Sent when a bank-transfer booking is created",
  "mt.10": "Payment reminder",
  "mt.11": "Sent about 12 hours before the transfer deadline",
  "mt.12": "Cancellation approved",
  "mt.13": "Sent when a cancellation is approved",
  "mt.14": "Check-in guide",
  "mt.15": "Sent after 10:00 the day before check-in",
  "mt.16": "Check-out guide",
  "mt.17": "Sent after 18:00 the day before check-out",
  "mt.18": "Passport request",
  "mt.19": "Automatically sends the upload URL to direct-booking guests",
  "mt.22": "Guest name",
  "mt.23": "Reservation no.",
  "mt.24": "Check-in date",
  "mt.25": "Check-out date",
  "mt.26": "Check-in time",
  "mt.27": "Check-out time",
  "mt.28": "Guests",
  "mt.29": "Total",
  "mt.30": "Property name",
  "mt.31": "Property URL",
  "mt.32": "Facility name",
  "mt.33": "Facility email",
  "mt.34": "Free cancellation until",
  "mt.35": "Transfer deadline (hours)",
  "mt.36": "Bank",
  "mt.37": "Branch",
  "mt.38": "Account type",
  "mt.39": "Account number",
  "mt.40": "Account holder",
  "mt.41": "Refund message (cancellation)",
  "mt.42": "Passport URL",
  "mt.43": "Failed to load ({p0})",
  "mt.44": "Failed to save ({p0})",
  "mt.45": "Timing saved. It takes effect from the next scheduled run (every hour at :00).",
  "mt.46": "Auto-send off",
  "mt.47": "Sent {p0} min after confirmation",
  "mt.48": "check-in",
  "mt.49": "check-out",
  "mt.50": "Sent {p1} {p0}, after {p2}:00",
  "mt.51": "Sent {p0} hours before the transfer deadline",
  "mt.52": "You have unsaved changes. Discard them and switch?",
  "mt.53": "Saved. It applies from the next email sent.",
  "mt.54": "Test send failed ({p0})",
  "mt.55": "Test email sent to {p0}.",
  "mt.56": "Email templates",
  "mt.57": "Edit the subject and body of emails sent automatically to guests.",
  "mt.58": "Loading…",
  "mt.59": "Not created",
  "mt.60": "Send timing",
  "mt.61": "Send automatically",
  "mt.62": "Trigger event",
  "mt.63": "After confirmation, ",
  "mt.64": "immediately",
  "mt.65": "{p0} h later",
  "mt.66": "{p0} min later",
  "mt.67": "",
  "mt.68": "",
  "mt.69": "or later",
  "mt.70": "* The scheduled job runs every hour at :00, so times are in whole hours (\"N minutes after confirmation\" is also sent at the next hourly run). Settings apply to all languages and properties.",
  "mt.71": " Automatic passport requests apply to direct bookings only (share manually via messages for OTA bookings).",
  "mt.72": "Revert",
  "mt.73": "Saving…",
  "mt.74": "Save timing",
  "mt.75": "Before the transfer deadline: ",
  "mt.76": " h before",
  "mt.77": "(Transfer deadline: ",
  "mt.78": " hours after booking)",
  "mt.79": "* The scheduled job runs every hour at :00, so times are in whole hours. Settings apply to all languages and properties.",
  "mt.80": " If a booking is confirmed after the send time, it is sent at the first scheduled run after confirmation.",
  "mt.81": "Subject",
  "mt.82": "Body",
  "mt.83": "Tips:",
  "mt.85": " or ",
  "mt.87": " become headings; ",
  "mt.89": " on 2 or more consecutive lines becomes a table. ",
  "mt.90": " is a divider.",
  "mt.91": "Insert fields (click to insert)",
  "mt.92": "Test send (uses the text being edited and a sample booking; no need to save)",
  "mt.93": "Sending…",
  "mt.94": "Send test",
  "mt.95": "Last updated:",
  "mt.96": "Save",
  "mt.97": "Preview (sample booking)",
  "mt.98": "Mobile",
  "mt.99": "Subject:",
};
const DICT: Record<AdminLang, Record<AdminKey, string>> = { ja, zh, en };

// どこからでも呼べる翻訳関数（フック不要）。言語を切り替えると管理画面のページは作り直されるので、
// 描画中に tx() を呼べば常に今の言語になる（AdminLayout の <Outlet key={lang} />）。
let CURRENT: AdminLang = detect();
export function currentAdminLang(): AdminLang { return CURRENT; }
export function tx(k: AdminKey, v?: Record<string, unknown>): string {
  let s = DICT[CURRENT][k] ?? ja[k] ?? String(k);
  if (v) for (const [a, b] of Object.entries(v)) s = s.split(`{${a}}`).join(b == null ? '' : String(b));
  return s;
}
// 曜日（日曜始まり）と日付ラベル: ja 10/28(水) / zh 10/28(周三) / en Wed 10/28
const DOW: Record<AdminLang, string[]> = {
  ja: ['日', '月', '火', '水', '木', '金', '土'],
  zh: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
};
export function dowName(d: number): string { return DOW[CURRENT][d]; }
// 月曜始まりの曜日見出し
export function weekdaysMon(): string[] { const w = DOW[CURRENT]; return [...w.slice(1), w[0]]; }
export function mdDow(ymd: string): string {
  const md = `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}`;
  const d = DOW[CURRENT][new Date(ymd + 'T00:00:00Z').getUTCDay()];
  return CURRENT === 'en' ? `${d} ${md}` : `${md}(${d})`;
}

interface Ctx { lang: AdminLang; setLang: (l: AdminLang) => void; at: (k: AdminKey, v?: Record<string, string | number>) => string; }
const AdminI18nCtx = createContext<Ctx>({ lang: 'ja', setLang: () => {}, at: (k) => ja[k] });
export const useAdminI18n = () => useContext(AdminI18nCtx);

export function AdminI18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<AdminLang>(detect);
  CURRENT = lang;
  const setLang = useCallback((l: AdminLang) => {
    try { localStorage.setItem(KEY, l); } catch { /* 無視 */ }
    setLangState(l);
  }, []);
  useEffect(() => {
    // 既存の t('admin.xxx') も合わせる。管理画面を離れたらゲスト画面は日本語に戻す
    void i18n.changeLanguage(lang);
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : lang;
    return () => { void i18n.changeLanguage('ja'); document.documentElement.lang = 'ja'; };
  }, [lang]);
  const value = useMemo<Ctx>(() => ({
    lang, setLang,
    at: (k, v) => {
      let s = DICT[lang][k] ?? ja[k] ?? String(k);
      if (v) for (const [a, b] of Object.entries(v)) s = s.split(`{${a}}`).join(b == null ? '' : String(b));
      return s;
    },
  }), [lang, setLang]);
  return <AdminI18nCtx.Provider value={value}>{children}</AdminI18nCtx.Provider>;
}

// 言語のドロップダウン（地球アイコン付き）
export function AdminLangSelect({ className = '' }: { className?: string }) {
  const { lang, setLang, at } = useAdminI18n();
  return (
    <label className={`admin-lang ${className}`}>
      <svg className="admin-lang-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
      <select value={lang} onChange={(e) => setLang(e.target.value as AdminLang)} aria-label={at('language')}>
        {ADMIN_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
    </label>
  );
}
